/**
 * Kess Berlin - Custom JavaScript
 * Contains custom functionality for the Kess theme
 *
 * @namespace window.Kess
 */

(function () {
  "use strict";

  // Initialize global Kess namespace
  window.Kess = window.Kess || {};

  /**
   * ============================================================================
   * KESS BUNDLES - Generic Bundle Handler
   * A registry-based system for handling different bundle types
   *
   * Multi-form support: Automatically detects and attaches to ALL product forms
   * on the page (main product form + sticky ATC bar form), ensuring bundle
   * functionality works consistently across all add-to-cart buttons.
   * ============================================================================
   */
  Kess.Bundles = {
    // Registry of bundle handlers
    handlers: {},

    // Active bundle instances (one per form per bundle type)
    instances: [],

    /**
     * Register a new bundle handler
     * @param {string} type - Bundle type identifier
     * @param {Object} handler - Handler configuration
     */
    register(type, handler) {
      this.handlers[type] = {
        selector: handler.selector,
        init: handler.init,
        getIncludeItems: handler.getIncludeItems,
        getProperties: handler.getProperties,
        onVariantChange: handler.onVariantChange,
        validate: handler.validate || (() => true),
        ...handler,
      };
    },

    /**
     * Initialize all registered bundle handlers
     */
    init() {
      Object.keys(this.handlers).forEach((type) => {
        const handler = this.handlers[type];
        const elements = document.querySelectorAll(handler.selector);

        elements.forEach((element) => {
          if (element.dataset.kessBundleReady === "true") return;
          try {
            const instances = this.createInstance(type, element, handler);
            if (instances && instances.length > 0) {
              element.dataset.kessBundleReady = "true";
              this.instances.push(...instances);
            }
          } catch (error) {
            console.error(`[Kess Bundles] Error initializing ${type}:`, error);
          }
        });
      });
    },

    /**
     * Create a bundle instance
     * @param {string} type - Bundle type
     * @param {HTMLElement} element - Data element
     * @param {Object} handler - Handler configuration
     * @returns {Array<Object>} Array of bundle instances (one per form)
     */
    createInstance(type, element, handler) {
      const data = this.parseData(element);
      if (!data) return [];

      // Find ALL product forms on the page (main form + sticky ATC bar form)
      const productId = element.dataset.productId;
      const productForms = productId
        ? document.querySelectorAll(
            `form[is="product-form"][data-product-id="${productId}"], sticky-atc-bar[data-product-id="${productId}"] form[is="product-form"]`,
          )
        : // Product card forms sell their own variant; binding one to the page's bundle would
          // submit the card's product as the bundle parent.
          [...document.querySelectorAll('form[is="product-form"]')].filter(
            (form) => !form.closest("[data-kess-b2b-scope]"),
          );
      if (!productForms.length) return [];

      const instances = [];

      // Create an instance for each form
      productForms.forEach((productForm) => {
        const instance = {
          type,
          element,
          data,
          handler,
          productForm,
          currentVariantId: null,
          currentMapping: null,
        };

        // Run handler's init if provided
        if (handler.init) {
          handler.init.call(instance, data);
        }

        // Bind events
        this.bindEvents(instance);

        // Initial mapping update
        this.updateMapping(instance);

        instances.push(instance);
      });

      return instances;
    },

    /**
     * Parse JSON data from element
     * @param {HTMLElement} element
     * @returns {Object|null}
     */
    parseData(element) {
      try {
        return JSON.parse(element.textContent);
      } catch (error) {
        console.error("[Kess Bundles] Error parsing data:", error);
        return null;
      }
    },

    /**
     * Bind events for a bundle instance
     * @param {Object} instance
     */
    bindEvents(instance) {
      // Listen for variant changes
      document.addEventListener("variant:changed", (event) => {
        this.handleVariantChange(instance, event);
      });

      // Override form submission
      instance.productForm.addEventListener(
        "submit",
        (event) => {
          this.handleFormSubmit(instance, event);
        },
        true,
      );
    },

    /**
     * Handle variant change
     * @param {Object} instance
     * @param {CustomEvent} event
     */
    handleVariantChange(instance, event) {
      const variantField = instance.productForm?.querySelector('[name="id"]');
      if (!variantField?.value) return;
      const variant = { id: variantField.value };

      instance.currentVariantId = variant.id;
      this.updateMapping(instance);

      // Call handler's onVariantChange if provided
      if (instance.handler.onVariantChange) {
        instance.handler.onVariantChange.call(instance, variant);
      }
    },

    /**
     * Update current variant ID from form
     * Handles both input[name="id"] (main form) and select[name="id"] (sticky bar)
     * @param {Object} instance
     */
    updateMapping(instance) {
      // Try to find variant ID input or select
      const variantField = instance.productForm?.querySelector('[name="id"]');
      if (!variantField) return;

      // Update current variant ID if not already set
      if (!instance.currentVariantId) {
        instance.currentVariantId = variantField.value;
      }
    },

    /**
     * Handle form submission
     * @param {Object} instance
     * @param {Event} event
     */
    handleFormSubmit(instance, event) {
      // Update variant ID from form at submit time (in case it changed)
      const variantField = instance.productForm?.querySelector('[name="id"]');
      if (variantField) {
        instance.currentVariantId = variantField.value;
      }

      // Validate if bundle should be processed
      if (!instance.handler.validate.call(instance)) {
        if (instance.handler.preventSubmitOnInvalid) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
        return; // Let normal submission proceed unless this handler explicitly owns invalid state
      }

      // Get include items from handler
      const includeItems = instance.handler.getIncludeItems ? instance.handler.getIncludeItems.call(instance) : [];

      if (!includeItems || includeItems.length === 0) {
        return; // Let normal submission proceed
      }

      // Check if cart type is page
      if (document.body.classList.contains("cart-template") || window.FoxTheme?.settings?.cartType === "page") {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      this.submitBundle(instance, includeItems);
    },

    /**
     * Submit bundle to cart
     * @param {Object} instance
     * @param {Array} includeItems - Array of variant IDs to include
     */
    async submitBundle(instance, includeItems) {
      const addToCartButton = instance.productForm.querySelector('[name="add"]');
      if (!addToCartButton) {
        console.warn("No add to cart button found");
        console.groupEnd();
        return;
      }

      // Disable button and show loading state
      addToCartButton.setAttribute("disabled", "disabled");
      addToCartButton.classList.add("btn--loading");

      const setId = Date.now().toString();
      const mainVariantId = parseInt(instance.currentVariantId);

      // Get quantity from form
      const quantityInput = instance.productForm.querySelector('input[name="quantity"]');
      const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;

      // Get custom properties from handler
      const customProperties = instance.handler.getProperties
        ? instance.handler.getProperties.call(instance, includeItems)
        : {};

      // Get bundle name from handler (defaults to bundle type)
      const bundleName = instance.handler.getBundleName ? instance.handler.getBundleName.call(instance) : instance.type;

      // Get global set ID from handler data (from metafield or variant SKU)
      const globalSetId = instance.handler.getGlobalSetId ? instance.handler.getGlobalSetId.call(instance) : setId;

      // Build the cart items with bundle properties
      // Required by Cart Transform: _includeItem, _setId, _globalSetId, _set_name
      const formData = {
        items: [
          {
            id: mainVariantId,
            quantity: quantity,
            properties: {
              _includeItem: includeItems.join(","),
              _setId: setId,
              _globalSetId: globalSetId,
              _set_name: bundleName,
              ...customProperties,
            },
          },
        ],
      };

      // Gather sections to bundle (same as original ProductForm)
      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:grouped-sections", { bubbles: true, detail: { sections: sectionsToBundle } }),
      );
      // Add sections to request for cart drawer rendering
      formData.sections = sectionsToBundle;
      formData.sections_url = window.location.pathname;

      // Build cart add URL (ensure .js extension for AJAX API)
      const ensureJsExtension = (url) => (url?.endsWith(".js") ? url : `${url}.js`);
      const cartAddUrl = ensureJsExtension(window.FoxTheme?.routes?.cart_add_url);

      try {
        const response = await fetch(cartAddUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify(formData),
        });

        if (response.ok) {
          const addResponse = await response.json();
          // IMPORTANT: /cart/add.js only returns added items, not full cart state
          // The cart drawer needs item_count, total_price etc. to properly render
          // So we fetch the full cart and merge the sections from the add response
          const cartResponse = await fetch(`${window.FoxTheme?.routes?.cart_url || "/cart"}.js`, {
            headers: { "Content-Type": "application/json" },
          });
          const cartState = await cartResponse.json();

          // Merge sections from add response into cart state
          cartState.sections = addResponse.sections;
          // Publish cart update event with full cart state + sections
          if (window.FoxTheme?.pubsub) {
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartState });
          } else {
            console.warn("FoxTheme.pubsub not available");
          }

          // Dispatch product added event
          document.dispatchEvent(
            new CustomEvent("product-ajax:added", {
              detail: { product: addResponse },
            }),
          );

          // Dispatch bundle added event
          this.dispatchBundleAddedEvent(instance, includeItems);

          // Show cart drawer
          const cartDrawer = document.querySelector("cart-drawer");
          if (cartDrawer && typeof cartDrawer.show === "function") {
            cartDrawer.show(addToCartButton);
          }
        } else {
          const errorData = await response.json();
          console.error("Error response:", errorData);
          this.handleCartError(instance, errorData);
        }
      } catch (error) {
        console.error("[Kess Bundles] Error adding bundle to cart:", error);
        this.handleCartError(instance, { description: "An error occurred. Please try again." });
      } finally {
        addToCartButton.removeAttribute("disabled");
        addToCartButton.classList.remove("btn--loading");
        console.groupEnd();
      }
    },

    /**
     * Handle cart error
     * @param {Object} instance
     * @param {Object} errorData
     */
    handleCartError(instance, errorData) {
      const errorMessage = errorData.description || errorData.message || "An error occurred";

      const errorElement = instance.productForm.querySelector(".product-form__error-message");
      if (errorElement) {
        errorElement.hidden = false;
        errorElement.textContent = errorMessage;
        setTimeout(() => {
          errorElement.hidden = true;
        }, 5000);
      } else {
        alert(errorMessage);
      }

      document.dispatchEvent(
        new CustomEvent("product-ajax:error", {
          detail: { errorMessage },
        }),
      );
    },

    /**
     * Dispatch analytics event when bundle is added
     * @param {Object} instance
     * @param {Array} includeItems
     */
    dispatchBundleAddedEvent(instance, includeItems) {
      const eventData = {
        event: "kess_product_bundle_added",
        bundle_type: instance.type,
        main_variant_id: instance.currentVariantId,
        include_items: includeItems,
      };

      if (window.Shopify?.analytics?.publish) {
        Shopify.analytics.publish(eventData.event, eventData);
      }

      document.dispatchEvent(
        new CustomEvent("kess:bundle-added", {
          detail: eventData,
        }),
      );
    },

    /**
     * Reinitialize (useful for theme editor or AJAX loaded content)
     */
    reinit() {
      this.instances = [];
      this.init();
    },
  };

  Kess.CartProperties = {
    init(root = document) {
      root.querySelectorAll('form[is="product-form"]').forEach((form) => {
        if (form.querySelector('[name="properties[_itemcategory]"]')) return;
        const productId = form.dataset.productId || form.closest("sticky-atc-bar")?.dataset.productId;
        if (
          productId &&
          document.querySelector(
            `[data-kess-product-bundle-data][data-product-id="${productId}"], [data-kess-set-components][data-product-id="${productId}"]`,
          )
        )
          return;

        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "properties[_itemcategory]";
        input.value = "singleproduct";
        form.appendChild(input);
      });
    },
  };

  Kess.Navigation = {
    init() {
      this.boundSetAnnouncementHeight = this.setAnnouncementHeight.bind(this);
      this.boundScheduleAnnouncementHeight = this.scheduleAnnouncementHeight.bind(this);
      this.setAnnouncementHeight();
      this.observeAnnouncementBar();
      this.bindMenuDrawerState();
      window.addEventListener("resize", this.boundSetAnnouncementHeight);
      window.addEventListener("scroll", this.boundSetAnnouncementHeight, { passive: true });
      document.addEventListener("shopify:section:load", () => {
        this.setAnnouncementHeight();
        this.observeAnnouncementBar();
        this.bindMenuDrawerState();
      });
    },

    setAnnouncementHeight() {
      const announcementBar = document.querySelector(".announcement-bar-section");

      if (!announcementBar) {
        if (this.announcementHeight !== 0) {
          document.documentElement.style.setProperty("--kess-announcement-height", "0px");
          this.announcementHeight = 0;
        }
        return;
      }

      const rect = announcementBar.getBoundingClientRect();
      const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));

      const nextHeight = Math.round(visibleHeight);
      if (nextHeight === this.announcementHeight) return;

      document.documentElement.style.setProperty("--kess-announcement-height", `${nextHeight}px`);
      this.announcementHeight = nextHeight;
    },

    scheduleAnnouncementHeight() {
      if (this.announcementHeightFrame) return;

      this.announcementHeightFrame = requestAnimationFrame(() => {
        this.announcementHeightFrame = null;
        this.setAnnouncementHeight();
      });
    },

    /**
     * Resize/scroll listeners miss height changes that happen without a window event:
     * announcement text wrapping onto a second line, the injected discount banner, or
     * late font loads. Observe the bar itself so --kess-announcement-height (and with it
     * the menu drawer's top inset) always tracks the real rendered height.
     */
    observeAnnouncementBar() {
      if (typeof ResizeObserver === "undefined") return;

      const announcementBar = document.querySelector(".announcement-bar-section");
      if (!announcementBar || announcementBar === this.observedAnnouncementBar) return;

      if (!this.announcementResizeObserver) {
        this.announcementResizeObserver = new ResizeObserver(this.boundScheduleAnnouncementHeight);
      } else {
        this.announcementResizeObserver.disconnect();
      }

      this.announcementResizeObserver.observe(announcementBar);
      this.observedAnnouncementBar = announcementBar;
    },

    bindMenuDrawerState() {
      const menuDrawer = document.querySelector("#MenuDrawer");
      if (!menuDrawer || typeof MutationObserver === "undefined") return;

      const syncState = () => {
        const isOpen = menuDrawer.hasAttribute("open");
        if (isOpen) this.setAnnouncementHeight();
        document.body.classList.toggle("kess-menu-drawer-open", isOpen);
      };

      syncState();

      const observer = new MutationObserver(syncState);
      observer.observe(menuDrawer, { attributes: true, attributeFilter: ["open"] });
    },
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      Kess.Navigation.init();
    });
  } else {
    Kess.Navigation.init();
  }

  /**
   * ============================================================================
   * DUO-STICK REFILL BUNDLE HANDLER
   * Handles duo-stick products with refill and case product mapping
   * ============================================================================
   */
  Kess.Bundles.register("product-bundle", {
    selector: "[data-kess-product-bundle-data]",

    /**
     * Find the duo/mono configurator on the page (if any). Its presence means we are on a
     * Duo/Mono PDP where the cart parent is the product's default variant and the selected
     * refill ids are published onto the configurator element.
     * @returns {HTMLElement|null}
     */
    getConfigurator() {
      if (!this.data.usesRefillConfigurator) return null;
      const selector = `[data-kess-duo-configurator][data-product-id="${this.element.dataset.productId}"]`;
      const scope = this.element.closest(".shopify-section") || document;
      return scope.querySelector(selector) || document.querySelector(selector);
    },

    /**
     * Read the published refill selection from the duo/mono configurator.
     * @returns {{ready: boolean, ids: string[]}|null} null when no configurator is present
     */
    getConfiguratorSelection() {
      const configurator = this.handler.getConfigurator.call(this);
      if (!configurator) return null;
      const raw = configurator.dataset.kessSelectedRefillIds || "";
      return {
        ready: configurator.dataset.kessSelectionReady === "true",
        ids: raw
          .split(",")
          .map((id) => id.trim())
          .filter(Boolean),
      };
    },

    /**
     * Perfume products carry a VARIANT-level custom.perfume_refill_product metafield, exposed as a
     * direct perfumeMap { mainVariantId: { id, available } }. When the selected variant has an
     * entry, that refill is added instead of the bare scent variant. No SKU/index mapping —
     * a variant without the metafield (e.g. a 2ml sample) has no entry and adds normally.
     * Returns the entry regardless of `available` — the OOS gate lives in applyPerfumeAvailability
     * (a disabled add button), not here (see validate()).
     * @returns {{id: (number|string), available: boolean}|null}
     */
    getPerfumeRefill() {
      const map = this.data.perfumeMap;
      if (!map) return null;
      return map[String(this.currentVariantId)] || null;
    },

    getProductRefill() {
      return this.data.productRefillMap?.[String(this.currentVariantId)] || null;
    },

    /**
     * Perfume out-of-stock guard. /cart/add does not validate _includeItem children, so an OOS
     * refill would silently oversell. Disable add-to-cart — the main product-form button AND the
     * sticky ATC bar button — when the selected scent variant's refill is out of stock. This is
     * the ONLY place perfume OOS is enforced (validate()/getIncludeItems intentionally don't, see
     * validate()). Runs on init (default variant) and on variant:changed.
     *
     * We ONLY ever ADD a disable — never remove one. product-info.js's updateButtonsState runs on
     * every variant change (and the server render on load) and owns both the enable transition and
     * the scent variant's OWN sold-out state. Removing `disabled` here would wrongly clear a
     * legitimate scent-OOS disable for a variant whose scent is sold out but whose refill is in
     * stock. No-op for in-stock refills, variants with no perfume entry, and non-perfume products.
     * @param {Object} [variant] - variant from the variant:changed event, if any.
     */
    applyPerfumeAvailability(variant) {
      const map = this.data.productRefillMap || this.data.perfumeMap;
      if (!map || !this.productForm) return;
      const field = this.productForm.querySelector('[name="id"]');
      const variantId = variant && variant.id != null ? variant.id : field && field.value;
      const entry = variantId != null ? map[String(variantId)] : null;
      if (!entry || entry.available !== false) return; // refill in stock / no entry → leave the theme's state
      const strings = window.FoxTheme?.variantStrings;

      // Refill OOS: add the disable on top of whatever the theme set. Main product-form button +
      // sticky ATC bar button (same selector product-info.js syncs).
      const productId = this.element.dataset.productId;
      [
        this.productForm.querySelector('[name="add"]'),
        document.querySelector(`sticky-atc-bar[data-product-id="${productId}"] [name="add"]`),
      ]
        .filter(Boolean)
        .forEach((button) => {
          button.setAttribute("disabled", "disabled");
          const label = button.querySelector("span:first-child");
          if (label && strings?.soldOut) label.textContent = strings.soldOut;
        });
    },

    init() {
      this.handler.applyPerfumeAvailability.call(this);
    },

    onVariantChange(variant) {
      this.handler.applyPerfumeAvailability.call(this, variant);
    },

    /**
     * Validate if this bundle should be processed:
     * - Duo/Mono/refill PDP: a complete + available configurator selection.
     * - Perfume PDP (normal picker): the selected variant has a perfume_refill_product entry.
     *
     * NOTE: the perfume branch intentionally does NOT check the refill's availability. An OOS
     * refill is blocked by disabling the add button (applyPerfumeAvailability). Returning false
     * here would instead let the form fall through to a plain add of the bare scent variant
     * (a bottle with no refill) — which is worse. Keep the availability gate on the button.
     * @returns {boolean}
     */
    validate() {
      const selection = this.handler.getConfiguratorSelection.call(this);
      if (selection) return selection.ready && selection.ids.length > 0;
      if (this.handler.getProductRefill.call(this)) return true;
      if (this.handler.getPerfumeRefill.call(this)) return true;
      const caseProduct = this.data.caseProduct;
      return !!(this.data.hasCaseProduct && caseProduct?.variantId && caseProduct?.variantAvailable);
    },

    /**
     * Get bundle name for Cart Transform
     * @returns {string}
     */
    getBundleName() {
      return this.data.bundleName || "";
    },

    /**
     * Get global set ID for Cart Transform
     * Uses metafield bundle_set_id if available, otherwise variant SKU
     * @returns {string}
     */
    getGlobalSetId() {
      return this.data.globalSetId || "";
    },

    /**
     * Variant IDs added as _includeItem on the parent line item:
     * - Configurator products: the selected refill ids (duo = two ids, duplicates allowed).
     * - Perfume products: the selected variant's perfume_refill_product variant (added instead
     *   of the bare scent variant, which remains the parent).
     * The case product variant is appended when present and available.
     * @returns {Array<number>}
     */
    getIncludeItems() {
      let refillIds = [];
      let allowCase = true;
      const selection = this.handler.getConfiguratorSelection.call(this);
      if (selection) {
        refillIds = selection.ids;
      } else {
        const mappedRefill = this.handler.getProductRefill.call(this) || this.handler.getPerfumeRefill.call(this);
        if (mappedRefill) {
          refillIds = [mappedRefill.id];
          // The 2ml fragrance size adds the refill but not the case.
          allowCase = mappedRefill.addCase !== false;
        }
      }

      const items = [];
      refillIds.forEach((id) => {
        const parsed = parseInt(id, 10);
        if (!Number.isNaN(parsed)) items.push(parsed);
      });

      const caseProduct = this.data.caseProduct;
      const caseAvailable = this.data.hasCaseProduct && caseProduct?.variantId && caseProduct?.variantAvailable;
      if (allowCase && caseAvailable) {
        items.push(caseProduct.variantId);
      }

      return items;
    },

    /**
     * Get custom properties for cart item
     * @returns {Object}
     */
    getProperties() {
      return {};
    },
  });

  /**
   * ============================================================================
   * ANNOUNCEMENT BAR - DYNAMIC DISCOUNT BANNER
   * ============================================================================
   */
  Kess.AnnouncementDiscount = {
    selectors: {
      config: "[data-kess-announcement-bar-config]",
      bar: "[data-kess-announcement-bar]",
      wrapper: ".swiper-wrapper",
      controls: ".slider-controls",
      hiddenControls: "announcement-bar__controls--hidden",
      dynamicSlide: "[data-dynamic-discount-slide]",
      staticDiscountSlide: "[data-static-discount-slide]",
      announcementContent: ".announcement-bar__content",
      announcementText: ".announcement-bar__text",
    },

    constants: {
      salt: "kess_security_salt_2024",
      cookieName: "discount_code",
      validationCacheKey: "announcement_bar_discount_validation_v1",
    },

    init(scope = document) {
      const configs = scope.querySelectorAll(this.selectors.config);
      if (!configs.length) return;

      configs.forEach((configEl) => {
        const section = configEl.closest(".announcement-bar");
        if (!section) return;
        this.initSection(section, configEl);
      });
    },

    initSection(section, configEl) {
      if (section.dataset.discountAnnouncementInitialized === "true") return;
      section.dataset.discountAnnouncementInitialized = "true";

      let config;
      try {
        config = JSON.parse(configEl.textContent);
      } catch (error) {
        console.error("[Kess Announcement Discount] Invalid config", error);
        return;
      }

      const announcementBar = section.querySelector(this.selectors.bar);
      const wrapper = announcementBar?.querySelector(this.selectors.wrapper);
      if (!announcementBar || !wrapper) return;

      this.applyDiscountBanner({ section, announcementBar, wrapper, config }).catch((error) => {
        console.error("[Kess Announcement Discount] Failed to initialize", error);
      });
    },

    hash(value) {
      return btoa(`${value}${this.constants.salt}`);
    },

    decodeChannels(data) {
      try {
        if (!data) return [];
        const binary = atob(data);
        const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
        return JSON.parse(new TextDecoder("utf-8").decode(bytes));
      } catch (_) {
        return [];
      }
    },

    getCookie(name) {
      const parts = `; ${document.cookie}`.split(`; ${name}=`);
      return parts.length === 2 ? parts.pop().split(";").shift() : "";
    },

    clearCookie(name) {
      const expired = "Thu, 01 Jan 1970 00:00:00 GMT";
      const host = window.location.hostname || "";
      document.cookie = `${name}=; expires=${expired}; path=/`;

      if (!host) return;

      document.cookie = `${name}=; expires=${expired}; path=/; domain=${host}`;
      const bareHost = host.replace(/^www\./i, "");
      if (bareHost) {
        document.cookie = `${name}=; expires=${expired}; path=/; domain=.${bareHost}`;
      }
    },

    extractDiscountFromPath(pathname) {
      const match = (pathname || "").match(/\/discount\/([^/?#]+)/i);
      if (!match) return null;

      try {
        return decodeURIComponent(match[1]);
      } catch (_) {
        return match[1];
      }
    },

    // Resolve the discount code and track WHERE it came from so the backend can
    // tell a fresh arrival apart from a remembered one. A code is remembered via
    // the validation cache (not a cookie / session key) — a code that arrived via
    // URL or Shopify's `discount_code` cookie is a one-time, "fresh" signal.
    getDiscountCodeContext(validationUrl) {
      const queryCode = new URLSearchParams(window.location.search).get("discount");
      if (queryCode) return { code: queryCode, source: "url", fresh: true };

      const pathCode = this.extractDiscountFromPath(window.location.pathname);
      if (pathCode) return { code: pathCode, source: "url", fresh: true };

      const cookieCode = this.getCookie(this.constants.cookieName);
      if (cookieCode) return { code: cookieCode, source: "cookie", fresh: true };

      const cached = this.getCachedDiscountCodeContext(validationUrl);
      if (cached) return cached;

      return { code: null, source: "none", fresh: false };
    },

    // Recall the most recently remembered code from the validation cache. Expired
    // entries are still surfaced (so the backend gets a chance to re-validate)
    // but flagged via the source so the caller forces a fresh check.
    getCachedDiscountCodeContext(validationUrl) {
      if (!validationUrl) return null;
      const prefix = `${(validationUrl || "").trim().toLowerCase()}::`;
      const store = this.getValidationStorage();

      let latest = null;
      Object.keys(store).forEach((key) => {
        if (key.indexOf(prefix) !== 0) return;
        const entry = store[key];
        if (!entry || !entry.code || !entry.value || entry.value.valid === false) return;
        if (!latest || (Number(entry.storedAt) || 0) > (Number(latest.storedAt) || 0)) {
          latest = entry;
        }
      });

      if (!latest) return null;
      const expired = !latest.expiresAt || latest.expiresAt < Date.now();
      return {
        code: latest.code,
        source: expired ? "validation_cache_expired" : "validation_cache",
        fresh: false,
      };
    },

    getRequestContext(discountCodeSource) {
      const params = new URLSearchParams(window.location.search);
      return {
        href: window.location.href || "",
        path: `${window.location.pathname}${window.location.search || ""}`,
        referrer: document.referrer || "",
        utm: {
          utm_source: params.get("utm_source") || "",
          utm_campaign: params.get("utm_campaign") || "",
          utm_medium: params.get("utm_medium") || "",
          utm_content: params.get("utm_content") || "",
          utm_term: params.get("utm_term") || "",
        },
        discountCodeSource: discountCodeSource || "unknown",
      };
    },

    getValidationStorage() {
      try {
        return JSON.parse(localStorage.getItem(this.constants.validationCacheKey)) || {};
      } catch (_) {
        return {};
      }
    },

    setValidationStorage(store) {
      try {
        localStorage.setItem(this.constants.validationCacheKey, JSON.stringify(store));
      } catch (_) {}
    },

    validationCacheKey(code, validationUrl) {
      return `${(validationUrl || "").trim().toLowerCase()}::${(code || "").trim().toUpperCase()}`;
    },

    getValidationCacheEntry(code, validationUrl) {
      const key = this.validationCacheKey(code, validationUrl);
      const store = this.getValidationStorage();
      const entry = store[key];
      if (!entry || !entry.expiresAt) return null;
      if (entry.expiresAt < Date.now()) {
        delete store[key];
        this.setValidationStorage(store);
        return null;
      }
      // Never serve a cached "invalid" verdict — only a positive result short-circuits.
      if (!entry.value || entry.value.valid === false) return null;
      return entry.value;
    },

    setValidationCacheEntry(code, validationUrl, value, hours) {
      const key = this.validationCacheKey(code, validationUrl);
      const ttlHours = Math.min(12, Math.max(1, Number(hours) || 12));
      // Single-entry store: only the most recently validated code is remembered,
      // so the bar never accumulates codes and always reflects just one code.
      this.setValidationStorage({
        [key]: {
          code,
          storedAt: Date.now(),
          expiresAt: Date.now() + ttlHours * 60 * 60 * 1000,
          value,
        },
      });
    },

    clearValidationCacheEntry(code, validationUrl) {
      const key = this.validationCacheKey(code, validationUrl);
      const store = this.getValidationStorage();
      if (Object.prototype.hasOwnProperty.call(store, key)) {
        delete store[key];
        this.setValidationStorage(store);
      }
    },

    clearValidationStore() {
      try {
        localStorage.removeItem(this.constants.validationCacheKey);
      } catch (_) {}
    },

    async validateDiscountCode(code, config, discountCodeSource) {
      if (!code) return { valid: false };
      if (!config.validationUrl) return { valid: true };

      const cached = this.getValidationCacheEntry(code, config.validationUrl);
      if (cached) return cached;

      try {
        const response = await fetch(config.validationUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code, context: this.getRequestContext(discountCodeSource) }),
        });

        if (!response.ok) {
          return { valid: false, message: "Validation request failed." };
        }

        const result = await response.json();
        // Only remember positive results; drop any stale entry on a negative one.
        if (result && result.valid) {
          this.setValidationCacheEntry(code, config.validationUrl, result, config.validationCacheHours);
        } else {
          this.clearValidationCacheEntry(code, config.validationUrl);
        }
        return result;
      } catch (error) {
        console.error("[Kess Announcement Discount] Validation failed", error);
        return { valid: false, message: "Validation request failed." };
      }
    },

    findChannel(code, channels) {
      if (!code || !Array.isArray(channels)) return null;
      const upperCode = code.toUpperCase();

      return (
        channels.find((channel) => {
          if (!Array.isArray(channel?.discount_codes)) return false;

          return channel.discount_codes.some((pattern) => {
            const upperPattern = String(pattern || "").toUpperCase();
            if (!upperPattern) return false;

            if (upperPattern.includes("*")) {
              const regexPattern = upperPattern.replace(/[.+?^${}()|[\]\\]/g, "\\$&").replace(/\*/g, ".*");
              return new RegExp(`^${regexPattern}$`).test(upperCode);
            }

            return this.hash(upperPattern) === this.hash(upperCode);
          });
        }) || null
      );
    },

    escapeHtml(value) {
      return String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\"/g, "&quot;")
        .replace(/'/g, "&#39;");
    },

    buildDiscountSlide({ message, code, backgroundColor, textColor }) {
      const slide = document.createElement("div");
      slide.className = "swiper-slide announcement-bar__dynamic-slide";
      slide.dataset.dynamicDiscountSlide = "true";
      slide.innerHTML = `
        <div
          class="announcement-bar__content announcement-bar__content--discount rte text-center block"
          style="--announcement-discount-bg:${this.escapeHtml(
            backgroundColor,
          )};--announcement-discount-color:${this.escapeHtml(textColor)};"
        >
          <p class="announcement-bar__text announcement-bar__text--discount">
            <span class="announcement-bar__discount-message">${this.escapeHtml(message)}</span>
            <span class="announcement-bar__discount-code">${this.escapeHtml(code)}</span>
          </p>
        </div>
      `;
      return slide;
    },

    ensureSlider(announcementBar, wrapper) {
      const slideCount = wrapper.children.length;
      if (slideCount <= 1 || announcementBar.carousel) return;
      delete announcementBar._cachedSlideItems;
      if (typeof announcementBar.initializeCarousel === "function") {
        announcementBar.initializeCarousel();
      }
    },

    prependDynamicSlide(announcementBar, wrapper, slide) {
      const swiper = announcementBar.carousel?.slider;

      // Fast path: builds that bundle Swiper's Manipulation module.
      if (swiper && typeof swiper.prependSlide === "function") {
        swiper.prependSlide(slide.outerHTML);
        swiper.update();
        swiper.slideToLoop(0, 0, false);
        return;
      }

      // This Swiper build does not include prependSlide. If a carousel is
      // already live we must tear it down and rebuild it — otherwise the new
      // slide ends up in the DOM in front of Swiper's loop clones while
      // Swiper's internal slide list stays stale, corrupting the indexing so
      // the arrows skip a slide (the "slides twice" bug).
      if (swiper && typeof swiper.destroy === "function") {
        swiper.destroy(true, true);
        announcementBar.carousel = null;
        wrapper.querySelectorAll(".swiper-slide-duplicate").forEach((dupe) => dupe.remove());
      }

      wrapper.insertBefore(slide, wrapper.firstChild);
      delete announcementBar._cachedSlideItems;
      this.ensureSlider(announcementBar, wrapper);

      // Show the freshly inserted discount slide first.
      const rebuilt = announcementBar.carousel?.slider;
      if (rebuilt && typeof rebuilt.slideToLoop === "function") {
        rebuilt.slideToLoop(0, 0, false);
      }
    },

    // Drop the static (theme-editor) discount slide(s) so an automatic code is
    // never shown alongside a static one. Uses Swiper's slide API when the
    // carousel is live (it handles the cloned loop slides), and falls back to a
    // teardown or plain DOM removal otherwise.
    removeStaticDiscountSlides(announcementBar, wrapper) {
      const staticSlides = Array.from(wrapper.querySelectorAll(this.selectors.staticDiscountSlide));
      if (!staticSlides.length) return;

      const swiper = announcementBar.carousel?.slider;

      if (swiper) {
        const indexes = staticSlides
          .map((slide) => parseInt(slide.getAttribute("data-swiper-slide-index"), 10))
          .filter((index) => !Number.isNaN(index));

        if (indexes.length && typeof swiper.removeSlide === "function") {
          swiper.removeSlide(indexes);
          swiper.update();
        } else if (typeof swiper.destroy === "function") {
          swiper.destroy(true, true);
          announcementBar.carousel = null;
          wrapper.querySelectorAll(".swiper-slide-duplicate").forEach((dupe) => dupe.remove());
          staticSlides.forEach((slide) => slide.remove());
        }
        delete announcementBar._cachedSlideItems;
        return;
      }

      staticSlides.forEach((slide) => slide.remove());
      delete announcementBar._cachedSlideItems;
    },

    renderDiscountBanner({ announcementBar, wrapper, payload }) {
      // Either/or: remove any static discount slide before showing the automatic one.
      this.removeStaticDiscountSlides(announcementBar, wrapper);

      const existingDynamicSlide = wrapper.querySelector(this.selectors.dynamicSlide);
      if (existingDynamicSlide) {
        existingDynamicSlide.remove();
      }

      announcementBar.classList.add("announcement-bar__wrapper--discount-active");
      announcementBar.style.setProperty("--content-width", "100%");

      const slide = this.buildDiscountSlide(payload);
      this.prependDynamicSlide(announcementBar, wrapper, slide);

      const controls = announcementBar.querySelector(this.selectors.controls);
      if (controls) {
        const realSlides = wrapper.querySelectorAll(".swiper-slide:not(.swiper-slide-duplicate)").length;
        controls.classList.toggle(this.selectors.hiddenControls, realSlides <= 1);
      }
    },

    async applyDiscountBanner({ announcementBar, wrapper, config }) {
      const context = this.getDiscountCodeContext(config.validationUrl);
      const code = context.code;
      if (!code) return;

      // A freshly-arriving code (URL or Shopify's cookie) resets the remembered
      // code and forces a fresh server check — so the bar never keeps a stale or
      // previously-remembered code, and the validation request is always sent.
      if (context.fresh) {
        this.clearValidationStore();
      }

      const validation = await this.validateDiscountCode(code, config, context.source);

      // Consume Shopify's one-time `discount_code` cookie once we've read it; the
      // valid code is remembered via the validation cache, not the cookie.
      if (context.source === "cookie") {
        this.clearCookie(this.constants.cookieName);
      }

      if (!validation?.valid) {
        this.clearCookie(this.constants.cookieName);
        this.clearValidationCacheEntry(code, config.validationUrl);
        return;
      }

      const channels = this.decodeChannels(config.obfuscatedChannels);
      const channel = this.findChannel(code, channels);
      const payload = {
        code,
        message: channel?.banner_text || config.defaultBanner?.text || "",
        backgroundColor: channel?.banner_background_color || config.defaultBanner?.backgroundColor || "#000000",
        textColor: channel?.banner_color || config.defaultBanner?.textColor || "#ffffff",
      };

      this.renderDiscountBanner({ announcementBar, wrapper, payload });
    },
  };

  Kess.ShopCountry = {
    init() {
      if (!this.shouldRun()) return;

      this.syncInitialCartAttribute();
      this.bindEvents();
    },

    shouldRun() {
      return Boolean(window.FoxTheme?.settings?.enableShopLanguageLogic);
    },

    getCountryFromUrl() {
      const routeRoot = window.Shopify?.routes?.root || "/";
      const marketPath = routeRoot
        .toLowerCase()
        .replace(/^\/+|\/+$/g, "")
        .split("/")
        .filter(Boolean)
        .pop();
      const country = marketPath?.slice(-2).toUpperCase();

      return /^[A-Z]{2}$/.test(country) ? country : "EU";
    },

    getStoredCountry() {
      try {
        return localStorage.getItem("sl");
      } catch (_) {
        return null;
      }
    },

    setStoredCountry(country) {
      try {
        localStorage.setItem("sl", country);
      } catch (_) {}
    },

    async getCart() {
      const cartUrl = `${window.FoxTheme?.routes?.cart_url || "/cart"}.js`;
      const response = await fetch(cartUrl, {
        ...window.FoxTheme?.utils?.fetchConfig?.("json", "GET"),
      });

      return response.json();
    },

    getCartShopCountry(cart) {
      return cart?.attributes?.shop_country || null;
    },

    async updateCartAttribute(country) {
      if (!country) return;

      const body = JSON.stringify({
        attributes: {
          shop_country: country,
        },
      });

      await fetch(window.FoxTheme?.routes?.cart_update_url || "/cart/update", {
        ...window.FoxTheme?.utils?.fetchConfig?.(),
        body,
      });
    },

    async syncInitialCartAttribute() {
      try {
        const currentCountry = this.getCountryFromUrl();
        const storedCountry = this.getStoredCountry();
        const cart = await this.getCart();
        const cartCountry = this.getCartShopCountry(cart);

        if (cartCountry) {
          this.currentShopCountry = cartCountry;
          this.setStoredCountry(cartCountry);
          return;
        }

        const fallbackCountry = storedCountry || currentCountry;

        if (fallbackCountry) {
          await this.updateCartAttribute(fallbackCountry);
          this.currentShopCountry = fallbackCountry;
          this.setStoredCountry(fallbackCountry);
        }
      } catch (_) {}
    },

    async syncCartAttribute(cart) {
      const selectedCountry = this.getCountryFromUrl();
      const cartCountry = this.getCartShopCountry(cart);
      const currentCountry = cartCountry || this.currentShopCountry || this.getStoredCountry();

      if (!selectedCountry || selectedCountry === currentCountry) return;

      await this.updateCartAttribute(selectedCountry);
      this.currentShopCountry = selectedCountry;
      this.setStoredCountry(selectedCountry);
    },

    queueCartAttributeUpdate(cart) {
      if (cart) this.pendingCart = cart;

      clearTimeout(this.updateTimer);
      this.updateTimer = setTimeout(() => {
        const pendingCart = this.pendingCart;
        this.pendingCart = null;
        this.syncCartAttribute(pendingCart).catch(() => {});
      }, 0);
    },

    bindEvents() {
      document.addEventListener("product-ajax:added", () => {
        this.queueCartAttributeUpdate();
      });

      window.FoxTheme?.pubsub?.subscribe?.(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
        const cartCountry = this.getCartShopCountry(event?.cart);

        if (cartCountry) {
          this.currentShopCountry = cartCountry;
          this.setStoredCountry(cartCountry);
        }

        this.queueCartAttributeUpdate(event?.cart);
      });
    },
  };

  /**
   * ============================================================================
   * DUO STICK PDP CONFIGURATOR
   * ============================================================================
   */
  Kess.DuoStickConfigurator = {
    parseJSON(element, fallback) {
      try {
        return element ? JSON.parse(element.textContent || "") : fallback;
      } catch (error) {
        console.error("[Kess Duo Stick] Invalid JSON data:", error);
        return fallback;
      }
    },

    formatMoney(cents) {
      const settings = window.FoxTheme?.settings;
      if (!window.FoxTheme?.Currency?.formatMoney || !settings?.moneyFormat) return "";

      if (settings.currencyCodeEnabled) {
        return window.FoxTheme.Currency.formatMoney(cents, settings.moneyFormat);
      }

      const spacedFormat = settings.moneyFormat.replace(/(\S)\{\{/g, "$1 {{").replace(/\}\}(\S)/g, "}} $1");
      return window.FoxTheme.Currency.formatMoney(cents, spacedFormat);
    },

    init(root = document) {
      root.querySelectorAll("[data-kess-duo-configurator]").forEach((configurator) => {
        if (configurator.dataset.kessDuoReady === "true") return;
        configurator.dataset.kessDuoReady = "true";
        this.create(configurator);
      });
    },

    create(configurator) {
      const dataEl = configurator.querySelector("[data-kess-duo-variants]");
      const variants = this.parseJSON(dataEl, []);
      const form = document.getElementById(configurator.dataset.productFormId);
      const submit = form?.querySelector('[name="add"]');
      const label = submit?.querySelector("span:first-child");
      const currentPrice = submit?.querySelector("[data-kess-submit-current-price]");
      const comparePrice = submit?.querySelector("[data-kess-submit-compare-price]");
      const isMonoMode = configurator.dataset.kessDuoMode === "mono";
      const state = { activeTab: 1, shade1: null, shade2: null, matchedParentVariant: null };
      const variantById = new Map(variants.map((variant) => [String(variant.id), variant]));
      const labels = configurator.dataset;
      const productId = configurator.dataset.productId;
      const sectionId = configurator.dataset.sectionId;
      const mediaMapEl = configurator.querySelector("[data-kess-duo-shade-media]");
      const shadeMediaMap = this.parseJSON(mediaMapEl, {});
      // The cart parent defaults to the product's default/selected variant; in mono mode a
      // title-matched main variant overrides it (see resolveMatchedParentVariant below).
      const defaultVariant =
        variants.find((variant) => String(variant.id) === String(configurator.dataset.kessDefaultVariantId)) ||
        variants.find((variant) => variant.available) ||
        variants[0] ||
        null;
      const defaultParentAvailable = configurator.dataset.kessDefaultVariantAvailable !== "false";

      const getProductForms = () => {
        const forms = [];
        if (form) forms.push(form);
        if (productId) {
          document
            .querySelectorAll(`sticky-atc-bar[data-product-id="${productId}"] form[is="product-form"]`)
            .forEach((productForm) => {
              if (!forms.includes(productForm)) forms.push(productForm);
            });
        }
        return forms;
      };

      const updateSubmitPrice = (variant) => {
        if (!variant || variant.price == null) return;
        const formattedPrice = this.formatMoney(variant.price);
        if (currentPrice && formattedPrice) currentPrice.textContent = formattedPrice;

        if (!comparePrice) return;
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          const formattedComparePrice = this.formatMoney(variant.compare_at_price);
          if (formattedComparePrice) comparePrice.textContent = formattedComparePrice;
          comparePrice.hidden = false;
        } else {
          comparePrice.hidden = true;
        }
      };

      // Single configurator only: the Liquid shade button carries the main product variant whose
      // title matches the shade (data-main-variant-id). When one exists it becomes the cart
      // parent so cart/checkout show the correct variant title. Duo sticks never match (their
      // buttons get no main variant id) and keep the default variant as parent.
      const resolveMatchedParentVariant = () => {
        if (!isMonoMode) return null;
        const matchedId = state.shade1 && state.shade1.mainVariantId;
        return matchedId ? variantById.get(String(matchedId)) || null : null;
      };

      // Keep every product form's hidden [name="id"] input (main form + sticky bar) pointing at
      // the matched main variant, falling back to the default variant when no title matches.
      const syncParentVariantField = (matchedVariant) => {
        const targetId = String(matchedVariant?.id || configurator.dataset.kessDefaultVariantId || "");
        if (!targetId) return;
        getProductForms().forEach((productForm) => {
          const variantField = productForm.querySelector('[name="id"]');
          if (variantField && variantField.value !== targetId) variantField.value = targetId;
        });
      };

      // Publish the selected refill variant ids and readiness onto the configurator element.
      // The product-bundle handler reads these to build _includeItem directly.
      const publishSelection = (complete, available) => {
        const ids = [];
        if (state.shade1?.id) ids.push(state.shade1.id);
        if (!isMonoMode && state.shade2?.id) ids.push(state.shade2.id);
        configurator.dataset.kessSelectedRefillIds = ids.join(",");
        configurator.dataset.kessSelectionReady = complete && available ? "true" : "false";
      };

      const showError = (message) => {
        const error = configurator.querySelector("[data-kess-duo-error]");
        if (!error) return;
        const messageEl = error.querySelector("[data-kess-duo-error-message]");
        if (messageEl) messageEl.textContent = message;
        else error.textContent = message;
        error.hidden = false;
      };

      const hideError = () => {
        const error = configurator.querySelector("[data-kess-duo-error]");
        if (error) error.hidden = true;
      };

      const setTab = (tab) => {
        let blocked = false;
        if (tab === 2 && !state.shade1) {
          showError(labels.errorFirst);
          tab = 1;
          blocked = true;
        }
        if (!blocked) hideError();
        state.activeTab = tab;
        if (!isMonoMode)
          configurator.querySelectorAll("[data-kess-duo-tab]").forEach((button) => {
            const tabNumber = Number(button.dataset.kessDuoTab);
            const active = tabNumber === tab;
            const selectedShade = tabNumber === 1 ? state.shade1 : state.shade2;
            button.classList.toggle("is-active", active);
            button.classList.toggle("is-selected", !!selectedShade);
            button.classList.toggle("is-selected-inactive", !!selectedShade && !active);
            button.setAttribute("aria-selected", active ? "true" : "false");
          });
        update();
      };

      const ensureSelectionGalleryZoom = (gallery) => {
        if (!gallery) return null;

        if (!gallery.kessZoomDataSource) {
          gallery.kessZoomDataSource = Array.from(
            gallery.querySelectorAll(".product__media-item:not(.swiper-slide-duplicate)"),
          ).flatMap((media) => {
            if (media.dataset.mediaType === "video" || media.dataset.mediaType === "external_video") {
              const zoomHtml =
                media.kessZoomHtml ||
                media.querySelector("template[data-kess-video-zoom-html]")?.innerHTML.trim() ||
                "";
              if (!zoomHtml) return [];
              return [
                {
                  id: media.dataset.mediaId,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${zoomHtml}</div>`,
                  mediaType: media.dataset.mediaType,
                },
              ];
            }

            return [
              {
                id: media.dataset.mediaId,
                src: media.dataset.src,
                width: Number(media.dataset.pswpWidth) || 1200,
                height: Number(media.dataset.pswpHeight) || 1200,
                mediaType: media.dataset.mediaType,
              },
            ];
          });
        }

        if (gallery.lightbox) {
          gallery.lightbox.options.dataSource = gallery.kessZoomDataSource;
          return gallery.lightbox;
        }

        if (window.FoxTheme?.PhotoSwipeLightbox && window.FoxTheme?.PhotoSwipe) {
          gallery.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
            dataSource: gallery.kessZoomDataSource,
            pswpModule: window.FoxTheme.PhotoSwipe,
            bgOpacity: 1,
            arrowPrev: false,
            arrowNext: false,
            zoom: false,
            close: false,
            counter: false,
            preloader: false,
            preload: [1, 4],
          });

          gallery.lightbox.addFilter("thumbEl", (thumbEl, itemData, index) => {
            if (gallery.__kessZoomThumbEl && gallery.__kessZoomThumbEl.isConnected) {
              return gallery.__kessZoomThumbEl;
            }

            const mediaId = itemData?.id;
            const mediaItem =
              mediaId != null
                ? gallery.querySelector(`.product__media-item[data-media-id="${CSS.escape(String(mediaId))}"]`)
                : gallery.querySelector(`.product__media-item[data-media-index="${index}"]`);

            return mediaItem?.querySelector("img") || thumbEl;
          });
          gallery.lightbox.addFilter("placeholderSrc", (placeholderSrc, { data }) => data.src || placeholderSrc);

          gallery.lightbox.on("afterInit", () => {
            const pswp = gallery.lightbox?.pswp;
            const root = pswp?.element;
            if (!root || root.__kessBackdropClose) return;

            root.__kessBackdropClose = true;
            root.addEventListener(
              "click",
              (event) => {
                if (
                  event.target.closest(
                    ".pswp__button, .pswp__top-bar, video-element, video, iframe, img, model-viewer, product-model, .media__poster, .media__play-button",
                  )
                ) {
                  return;
                }

                if (
                  event.target.closest(
                    ".pswp__bg, .pswp__item, .pswp__zoom-wrap, .pswp__item--video, .pswp__item--external_video, .pswp__item--model",
                  )
                ) {
                  pswp.close();
                }
              },
              true,
            );
          });
          gallery.lightbox.on("uiRegister", () => {
            if (gallery.kessZoomDataSource.length > 1) {
              gallery.lightbox.pswp.ui.registerElement({
                name: "next",
                ariaLabel: "Next slide",
                order: 3,
                isButton: true,
                html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
                onClick: () => gallery.lightbox?.pswp?.next(),
              });
              gallery.lightbox.pswp.ui.registerElement({
                name: "prev",
                ariaLabel: "Previous slide",
                order: 1,
                isButton: true,
                html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
                onClick: () => gallery.lightbox?.pswp?.prev(),
              });
            }
            gallery.lightbox.pswp.ui.registerElement({
              name: "close-zoom",
              ariaLabel: "Close zoom image",
              order: 2,
              isButton: true,
              html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
              onClick: () => gallery.lightbox?.pswp?.close(),
            });
          });
          gallery.lightbox.init();
        }

        return gallery.lightbox || null;
      };

      const recoverSelectionGalleryHeight = (gallery) => {
        const wrapper = gallery?.querySelector("[data-kess-duo-selection-slides]");
        if (!wrapper) return;
        if (wrapper.style.height === "0px") {
          wrapper.style.height = "";
        }
        if (wrapper.getBoundingClientRect().height > 0) return;
        const card = wrapper.querySelector(".kess-shade-gallery__card");
        const cardHeight = card ? Math.ceil(card.getBoundingClientRect().height) : 0;
        if (cardHeight > 0) {
          wrapper.style.height = `${cardHeight}px`;
        }
      };

      const resetSelectionGallery = (gallery) => {
        if (!gallery) return;
        gallery.kessZoomDataSource = null;
        gallery.__kessZoomThumbEl = null;
        gallery.__kessDuoRenderedShadeId = null;

        const lightbox = gallery.lightbox;
        if (lightbox && typeof lightbox.destroy === "function") {
          if (lightbox.pswp && !lightbox.pswp.isDestroying && typeof lightbox.pswp.close === "function") {
            lightbox.pswp.close();
          }
          lightbox.destroy();
        }
        gallery.lightbox = null;

        const slider = gallery.sliderInstance && gallery.sliderInstance.slider;
        if (slider && typeof slider.destroy === "function") {
          slider.destroy(true, true);
          gallery.sliderInstance = false;
        }
      };

      const ensureSelectionGallerySlider = (gallery) => {
        if (!gallery || !window.FoxTheme?.Carousel) return;
        const viewer = gallery.querySelector('[id^="GalleryViewer"]');
        const initialized = viewer?.classList.contains("swiper-initialized");
        if (!viewer || initialized || gallery.sliderInstance?.slider) return;

        const mediaItemGap = parseInt(gallery.dataset.mediaItemGap || "0", 10);
        gallery.sliderInstance = new window.FoxTheme.Carousel(viewer, {
          init: false,
          slidesPerView: "auto",
          spaceBetween: Number.isNaN(mediaItemGap) ? 0 : mediaItemGap,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: false,
          threshold: 2,
        });
        gallery.sliderInstance.init();
        if (!gallery.sliderInstance.slider) {
          gallery.sliderInstance = false;
          return;
        }
        gallery.sliderInstance.slider.init();
      };

      const buildSelectionGallerySlide = (item, index, selected, template) => {
        const slide = template?.content?.firstElementChild?.cloneNode(true);
        if (!slide) return null;

        const mediaId = String(item.id || `${selected.id}-${index}`);
        const zoomSrc = item.zoom_src || item.zoomSrc || item.src || selected.image || "";
        const previewSrc = item.src || item.preview || selected.image || "";
        const alt = item.alt || selected.title || "";
        const width = Number(item.width) || 1200;
        const height = Number(item.height) || 1200;

        const mediaType = item.type || "image";
        const previewHtml = item.html || item.preview_html || "";
        const zoomHtml = item.zoom_html || "";

        slide.kessZoomHtml = zoomHtml;
        slide.dataset.mediaId = mediaId;
        slide.dataset.mediaType = mediaType;
        slide.dataset.mediaAlt = alt;
        slide.dataset.mediaIndex = String(index);
        slide.dataset.src = zoomSrc;
        slide.dataset.pswpWidth = String(width);
        slide.dataset.pswpHeight = String(height);

        const isVideo = mediaType === "video" || mediaType === "external_video";
        const image = slide.querySelector("[data-kess-duo-slide-image]");
        if (image) {
          const previewWidth = Math.min(width, 400);
          const previewHeight = Math.max(1, Math.round((previewWidth * height) / width));
          image.src = previewSrc;
          image.alt = alt;
          image.width = previewWidth;
          image.height = previewHeight;

          if (isVideo && previewHtml) {
            const previewTemplate = document.createElement("template");
            previewTemplate.innerHTML = previewHtml;
            const previewElement = previewTemplate.content.firstElementChild;
            if (previewElement) image.replaceWith(previewElement);
          }
        }

        const card = slide.querySelector("[data-kess-shade-zoom-target]");
        if (card) {
          card.dataset.mediaType = mediaType;
        }

        const playIndicator = slide.querySelector("[data-kess-duo-slide-play-indicator]");
        if (playIndicator) {
          playIndicator.hidden = true;
        }

        if (isVideo && zoomHtml) {
          const zoomTemplate = document.createElement("template");
          zoomTemplate.setAttribute("data-kess-video-zoom-html", "");
          zoomTemplate.innerHTML = zoomHtml;
          slide.appendChild(zoomTemplate);
        }

        const zoomButton = slide.querySelector("[data-kess-duo-slide-zoom]");
        if (zoomButton) {
          zoomButton.dataset.mediaId = mediaId;
          zoomButton.dataset.mediaType = mediaType;
          zoomButton.dataset.mediaIndex = String(index);
        }

        const hiddenLabel = slide.querySelector("[data-kess-duo-slide-label]");
        if (hiddenLabel) hiddenLabel.textContent = `Open media ${index + 1}`;

        return slide;
      };

      const updateSelectionPanel = () => {
        const selected = state.activeTab === 1 ? state.shade1 : state.shade2;
        const panel = configurator.querySelector("[data-kess-duo-selection]");
        if (!panel) return;

        const gallery = panel.querySelector("media-gallery");
        const slides = panel.querySelector("[data-kess-duo-selection-slides]");
        const slideTemplate = panel.querySelector("[data-kess-duo-selection-slide-template]");

        if (!selected) {
          panel.hidden = true;
          if (slides) slides.innerHTML = "";
          resetSelectionGallery(gallery);
          return;
        }

        panel.hidden = false;
        const title = panel.querySelector("[data-kess-duo-selection-title]");
        const description = panel.querySelector("[data-kess-duo-selection-description]");
        if (title) title.textContent = selected.title || "";
        if (description) description.textContent = selected.description || "";

        if (!gallery || !slides) return;
        if (gallery.__kessDuoRenderedShadeId === selected.id && !gallery.hidden && slides.children.length > 0) {
          return;
        }

        const items = shadeMediaMap[selected.id] || [];
        resetSelectionGallery(gallery);
        slides.innerHTML = "";
        items.forEach((item, index) => {
          const slide = buildSelectionGallerySlide(item, index, selected, slideTemplate);
          if (slide) slides.appendChild(slide);
        });
        gallery.dataset.onlyImage = items.length === 1 ? "true" : "false";
        gallery.hidden = items.length === 0;
        gallery.__kessDuoRenderedShadeId = selected.id;

        requestAnimationFrame(() => {
          if (typeof gallery.init === "function") gallery.init();
          ensureSelectionGallerySlider(gallery);
          ensureSelectionGalleryZoom(gallery);
          const slider = gallery.sliderInstance && gallery.sliderInstance.slider;
          recoverSelectionGalleryHeight(gallery);
          if (slider && typeof slider.update === "function") slider.update();
          requestAnimationFrame(() => recoverSelectionGalleryHeight(gallery));
        });
      };

      const updateStockIndicator = (selectionComplete, allAvailable) => {
        if (!sectionId) return;
        const block = document.getElementById(`Inventory-${sectionId}`);
        if (!block) return;
        if (!selectionComplete) {
          block.classList.add("kess-inventory-deferred");
          return;
        }
        block.classList.remove("kess-inventory-deferred");
        const status =
          block.querySelector("[data-kess-inventory-status]") || block.querySelector(".product__inventory");
        const textEl =
          block.querySelector("[data-kess-inventory-text]") || block.querySelector(".product__inventory-text");
        if (!status || !textEl) return;
        status.classList.remove(
          "product__inventory--in-stock",
          "product__inventory--low-stock",
          "product__inventory--out-of-stock",
        );
        status.classList.add(allAvailable ? "product__inventory--in-stock" : "product__inventory--out-of-stock");
        const messages = block.dataset;
        textEl.textContent = allAvailable
          ? messages.kessInStockMessage || textEl.dataset.inStock || textEl.textContent
          : messages.kessOutOfStockMessage || textEl.dataset.outOfStock || textEl.textContent;
        const bar = block.querySelector("progress-bar");
        if (bar) bar.hidden = true;
      };

      const update = () => {
        if (!isMonoMode)
          [1, 2].forEach((tab) => {
            const selectedShade = tab === 1 ? state.shade1 : state.shade2;
            const labelEl = configurator.querySelector(`[data-kess-duo-tab-label='${tab}']`);
            const mediaEl = configurator.querySelector(`[data-kess-duo-tab-selected='${tab}']`);
            const placeholderEl = configurator.querySelector(`[data-kess-duo-tab-placeholder='${tab}']`);
            if (labelEl)
              labelEl.textContent = selectedShade?.title || (tab === 1 ? labels.labelFirst : labels.labelSecond);
            if (placeholderEl) placeholderEl.hidden = !!selectedShade;
            if (mediaEl) {
              mediaEl.hidden = !selectedShade;
              mediaEl.innerHTML = "";
              if (selectedShade?.swatchStyle) {
                const swatch = document.createElement("span");
                swatch.className = "kess-duo-tab__media swatch-color block";
                swatch.style.cssText = selectedShade.swatchStyle;
                mediaEl.appendChild(swatch);
              }
            }
          });
        configurator.querySelectorAll("[data-kess-duo-tab]").forEach((button) => {
          const tabNumber = Number(button.dataset.kessDuoTab);
          const active = tabNumber === state.activeTab;
          const selectedShade = tabNumber === 1 ? state.shade1 : state.shade2;
          button.classList.toggle("is-selected", !!selectedShade);
          button.classList.toggle("is-selected-inactive", !!selectedShade && !active);
        });

        if (!isMonoMode)
          configurator.querySelectorAll("[data-kess-duo-change]").forEach((el) => {
            const tab = Number(el.dataset.kessDuoChange);
            const hasSelectedShade = tab === 1 ? state.shade1 : state.shade2;
            el.hidden = !hasSelectedShade || tab === state.activeTab;
          });

        configurator.querySelectorAll("[data-kess-duo-shade]").forEach((button) => {
          const current = state.activeTab === 1 ? state.shade1 : state.shade2;
          const selected = current?.id === button.dataset.shadeId;
          button.classList.toggle("is-selected", selected);
          button.setAttribute("aria-pressed", selected ? "true" : "false");
        });

        const selectionComplete = !!state.shade1 && (isMonoMode || !!state.shade2);
        state.matchedParentVariant = resolveMatchedParentVariant();
        syncParentVariantField(state.matchedParentVariant);
        const parentAvailable = state.matchedParentVariant
          ? state.matchedParentVariant.available !== false
          : defaultParentAvailable;
        const allShadesAvailable =
          selectionComplete &&
          state.shade1.available !== false &&
          (isMonoMode || state.shade2.available !== false) &&
          parentAvailable;
        if (selectionComplete) updateSubmitPrice(state.matchedParentVariant || defaultVariant);
        publishSelection(selectionComplete, allShadesAvailable);
        updateStockIndicator(selectionComplete, allShadesAvailable);

        if (submit && label) {
          const isNextStep = !isMonoMode && state.shade1 && state.activeTab === 1 && !state.shade2;
          const isIncompleteCurrentStep = !state.shade1 || (!isMonoMode && state.activeTab === 2 && !state.shade2);
          const isUnavailable = selectionComplete && !allShadesAvailable;
          const isVisuallyDisabled = !isNextStep && (isIncompleteCurrentStep || isUnavailable);

          submit.classList.toggle("kess-duo-submit--disabled", isVisuallyDisabled);
          if (isVisuallyDisabled) submit.setAttribute("aria-disabled", "true");
          else submit.removeAttribute("aria-disabled");

          if (!state.shade1) label.textContent = labels.labelSelectFirst;
          else if (isNextStep) label.textContent = labels.labelNext;
          else if (!isMonoMode && !state.shade2) label.textContent = labels.labelSelectSecond;
          else label.textContent = labels.labelAdd;
        }
        updateSelectionPanel();
      };

      const getShadeFromButton = (button) => ({
        id: button.dataset.shadeId,
        mainVariantId: button.dataset.mainVariantId || "",
        title: button.dataset.shadeTitle,
        option: button.dataset.shadeOption || button.dataset.shadeTitle,
        category: button.dataset.shadeCategory,
        description: button.dataset.shadeDescription,
        image: button.dataset.shadeImage,
        swatchStyle: button.dataset.shadeSwatchStyle,
        available: button.dataset.shadeAvailable !== "false",
      });

      const selectShadeButton = (button) => {
        if (!button) return false;
        const shade = getShadeFromButton(button);
        if (isMonoMode || state.activeTab === 1) state.shade1 = shade;
        else state.shade2 = shade;
        hideError();
        update();
        return true;
      };

      // Preselection metafields hold refill variant ids, and the shade buttons
      // are keyed by the refill variant, so match them directly.
      const findPreselectedShadeButton = (variantId) => {
        const id = String(variantId || "");
        if (!id) return null;
        return (
          Array.from(configurator.querySelectorAll("[data-kess-duo-shade]")).find(
            (button) => button.dataset.shadeId === id && button.dataset.shadeAvailable !== "false",
          ) || null
        );
      };

      const applyPreselectedVariants = () => {
        const ids = (configurator.dataset.preselectedVariantIds || "")
          .split(",")
          .map((id) => id.trim())
          .filter(Boolean);
        if (!ids.length) return;

        state.activeTab = 1;
        const first = findPreselectedShadeButton(ids[0]);
        if (first) selectShadeButton(first);

        if (!isMonoMode && ids[1] && state.shade1) {
          state.activeTab = 2;
          const second = findPreselectedShadeButton(ids[1]);
          if (second) selectShadeButton(second);
        }

        state.activeTab = !isMonoMode && state.shade2 ? 2 : 1;
        hideError();
        update();
      };

      // Main-product variant ids require kess_shade=1 to preselect a swatch.
      // Refill ids work directly via ?variant= or ordered ?variants= for two shades.
      const applyUrlSelection = () => {
        const buttons = Array.from(configurator.querySelectorAll("[data-kess-duo-shade]"));

        let fromSwatch = false;
        let urlVariant = "";
        let urlVariantIds = [];
        try {
          const params = new URLSearchParams(window.location.search);
          fromSwatch = params.get("kess_shade") === "1";
          urlVariant = params.get("variant") || "";
          const refillVariant = findPreselectedShadeButton(urlVariant);
          urlVariantIds = (params.get("variants") || (refillVariant ? urlVariant : ""))
            .split(",")
            .map((id) => id.trim())
            .filter((id) => /^\d+$/.test(id));
        } catch (error) {
          fromSwatch = false;
          urlVariant = "";
          urlVariantIds = [];
        }
        if (fromSwatch && urlVariant) {
          const matched = buttons.find((button) => button.dataset.mainVariantId === urlVariant);
          if (matched) {
            selectShadeButton(matched);
            return;
          }
        }

        if (urlVariantIds.length) {
          // Refill variant ids are what these links carry, but accept a main-product variant id
          // too so an id from the other space resolves instead of silently doing nothing. Kept
          // local to the url path: the metafield path above stays refill-ids-only by design.
          const findUrlShadeButton = (id) =>
            findPreselectedShadeButton(id) ||
            buttons.find(
              (button) => button.dataset.mainVariantId === id && button.dataset.shadeAvailable !== "false",
            ) ||
            null;
          const matched = urlVariantIds
            .map((id) => findUrlShadeButton(id))
            .filter(Boolean)
            .slice(0, isMonoMode ? 1 : 2);
          if (matched.length) {
            // The url outranks a preselect metafield applied moments ago, and replaces it whole
            // rather than filling only the slots it covers.
            state.shade1 = null;
            state.shade2 = null;
            state.activeTab = 1;
            selectShadeButton(matched[0]);
            if (matched[1]) {
              state.activeTab = 2;
              selectShadeButton(matched[1]);
            }
            state.activeTab = !isMonoMode && state.shade2 ? 2 : 1;
            hideError();
            update();
            return;
          }
        }
      };

      configurator.addEventListener(
        "click",
        (event) => {
          const trigger = event.target.closest(
            "[data-kess-duo-selection-media] .js-photoswipe--zoom, [data-kess-duo-selection-media] [data-kess-shade-zoom-target]",
          );
          if (!trigger || !configurator.contains(trigger)) return;
          const gallery = trigger.closest("media-gallery");
          const mediaItem = trigger.closest(".product__media-item");
          if (!gallery || !mediaItem) {
            return;
          }

          const lightbox = ensureSelectionGalleryZoom(gallery);
          if (!lightbox) {
            return;
          }

          const mediaId = mediaItem.dataset.mediaId;
          const index = (gallery.kessZoomDataSource || []).findIndex((item) => String(item.id) === String(mediaId));
          if (index < 0) {
            return;
          }

          event.preventDefault();
          event.stopImmediatePropagation();
          gallery.__kessZoomThumbEl = mediaItem.querySelector("img");

          const slider = gallery.sliderInstance && gallery.sliderInstance.slider;
          if (slider) {
            if (slider.params && slider.params.loop && typeof slider.slideToLoop === "function") {
              slider.slideToLoop(index, 0, false);
            } else if (typeof slider.slideTo === "function") {
              slider.slideTo(index, 0, false);
            }
            if (typeof slider.updateSlidesClasses === "function") {
              slider.updateSlidesClasses();
            }
          }

          const initialPointerPos = event.clientX || event.clientY ? { x: event.clientX, y: event.clientY } : null;
          lightbox.loadAndOpen(index, undefined, initialPointerPos);
        },
        true,
      );

      configurator.addEventListener("click", (event) => {
        const tabButton = event.target.closest("[data-kess-duo-tab]");
        if (tabButton) {
          setTab(Number(tabButton.dataset.kessDuoTab));
          return;
        }

        const shadeButton = event.target.closest("[data-kess-duo-shade]");
        if (!shadeButton) return;
        selectShadeButton(shadeButton);
      });

      const handleSubmit = (event) => {
        if (!state.shade1) {
          event.preventDefault();
          event.stopImmediatePropagation();
          showError(labels.errorFirst);
          update();
          return;
        }
        if (!isMonoMode && state.activeTab === 1 && !state.shade2) {
          event.preventDefault();
          event.stopImmediatePropagation();
          setTab(2);
          return;
        }
        if (!isMonoMode && !state.shade2) {
          event.preventDefault();
          event.stopImmediatePropagation();
          showError(labels.errorSecond);
          update();
          return;
        }
        const parentAvailable = state.matchedParentVariant
          ? state.matchedParentVariant.available !== false
          : defaultParentAvailable;
        const available =
          state.shade1.available !== false && (isMonoMode || state.shade2.available !== false) && parentAvailable;
        if (!available) {
          event.preventDefault();
          event.stopImmediatePropagation();
          showError(labels.errorUnavailable);
          update();
        }
      };

      getProductForms().forEach((productForm) => {
        productForm.addEventListener("submit", handleSubmit, true);
      });

      if (defaultVariant) updateSubmitPrice(defaultVariant);
      applyPreselectedVariants();
      applyUrlSelection();
      update();
    },
  };

  /**
   * ============================================================================
   * SET COMPONENTS PDP SELECTOR
   * ============================================================================
   */
  Kess.SetComponents = {
    parseJSON(element, fallback) {
      try {
        return element ? JSON.parse(element.textContent || "") : fallback;
      } catch (error) {
        console.error("[Kess Set Components] Invalid JSON data:", error);
        return fallback;
      }
    },

    normalize(value) {
      return String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, "");
    },

    // A shade-analysis result page deep-links a set PDP as ?variants=<id>,<id>,... holding the
    // numeric variant ids the customer was recommended. Ids are deliberately NOT deduped: a duo
    // may legitimately carry the same shade in both slots, and each occurrence is claimed once.
    getUrlVariantIds() {
      try {
        return (new URLSearchParams(window.location.search).get("variants") || "")
          .split(",")
          .map((id) => id.trim())
          .filter((id) => /^\d+$/.test(id));
      } catch (error) {
        return [];
      }
    },

    // The url list carries no component identity and its order need not match the set's own
    // component order, so each component claims (and removes) only the ids it can actually
    // render: two for a duo, one otherwise. Claiming from one shared pool makes url order
    // irrelevant ACROSS components while keeping it meaningful WITHIN a duo (first claimed id
    // becomes shade 1), and stops two components sharing one refill product from both taking
    // the same id.
    consumeUrlVariantIds(component, pool) {
      if (!pool.length) return [];
      const capacity = component.dataset.componentType === "duo" ? 2 : 1;
      const selectable = Array.from(component.querySelectorAll("[data-kess-set-shade]")).filter(
        (button) => button.dataset.shadeAvailable !== "false",
      );
      if (!selectable.length) return [];

      const claimed = [];
      let index = 0;
      while (index < pool.length && claimed.length < capacity) {
        const id = pool[index];
        const renderable = selectable.some(
          (button) => button.dataset.shadeId === id || button.dataset.targetVariantId === id,
        );
        if (renderable) claimed.push(pool.splice(index, 1)[0]);
        else index += 1;
      }
      return claimed;
    },

    init(root = document) {
      root.querySelectorAll("[data-kess-set-components]").forEach((set) => {
        if (set.dataset.kessSetReady === "true") return;
        set.dataset.kessSetReady = "true";
        this.create(set);
      });
    },

    getRoot(scope = document) {
      return scope.querySelector("[data-kess-set-components]");
    },

    create(root) {
      const urlVariantIds = this.getUrlVariantIds();
      root
        .querySelectorAll("[data-kess-set-component]")
        .forEach((component) =>
          this.createComponent(root, component, this.consumeUrlVariantIds(component, urlVariantIds)),
        );
      this.updateSubmit(root);
    },

    createComponent(root, component, urlVariantIds) {
      component.__kessSet = {
        activeTab: 1,
        shade1: null,
        shade2: null,
        selectedVariant: null,
        variants: this.parseJSON(component.querySelector("[data-kess-set-product-variants]"), []),
        variantImages: this.parseJSON(component.querySelector("[data-kess-set-variant-images]"), {}),
        componentBundle: this.parseJSON(component.querySelector("[data-kess-set-component-bundle]"), null),
        perfumeMap: this.parseJSON(component.querySelector("[data-kess-set-perfume-map]"), null),
        mediaMap: this.parseJSON(component.querySelector("[data-kess-duo-shade-media]"), {}),
      };

      this.applyPreselectedVariants(root, component, urlVariantIds);

      component.addEventListener("click", (event) => {
        const tab = event.target.closest("[data-kess-set-tab]");
        if (tab && component.contains(tab)) {
          this.setTab(root, component, Number(tab.dataset.kessSetTab));
          return;
        }

        const next = event.target.closest("[data-kess-set-next]");
        if (next && component.contains(next)) {
          this.setTab(root, component, 2);
          return;
        }

        const shade = event.target.closest("[data-kess-set-shade]");
        if (!shade || !component.contains(shade)) return;
        this.selectShade(root, component, shade);
      });

      this.updateComponent(root, component);
    },

    getLabels(root) {
      return root.dataset;
    },

    getProductForms(root) {
      const forms = [];
      const form = document.getElementById(root.dataset.productFormId);
      if (form) forms.push(form);
      if (root.dataset.productId) {
        document
          .querySelectorAll(`sticky-atc-bar[data-product-id="${root.dataset.productId}"] form[is="product-form"]`)
          .forEach((productForm) => {
            if (!forms.includes(productForm)) forms.push(productForm);
          });
      }
      return forms;
    },

    setTab(root, component, tab) {
      const state = component.__kessSet;
      const labels = this.getLabels(root);
      if (tab === 2 && !state.shade1) {
        this.showError(component, labels.errorFirst);
        this.scrollTo(component.querySelector("[data-kess-set-error]"));
        tab = 1;
      } else {
        this.hideError(component);
      }
      state.activeTab = tab;
      this.updateComponent(root, component);
    },

    // Ids claimed from the url replace the merchant's preselected_variants metafield outright:
    // a customer arriving from their own shade analysis should see what was recommended to them,
    // not the shop default. Components the url could not cover keep the metafield behaviour, and
    // the two sources are never blended within one component — a half-covered duo prompts for
    // the missing shade rather than presenting a shop default as if it were recommended.
    applyPreselectedVariants(root, component, urlVariantIds) {
      const raw = component.dataset.preselectedVariantIds || "";
      const ids =
        urlVariantIds && urlVariantIds.length
          ? urlVariantIds
          : raw
              .split(",")
              .map((id) => id.trim())
              .filter(Boolean);
      if (!ids.length) return;

      const mode = component.dataset.componentType;
      const state = component.__kessSet;

      const candidates = Array.from(component.querySelectorAll("[data-kess-set-shade]")).filter(
        (button) => button.dataset.shadeAvailable !== "false",
      );

      // Direct id match covers normal components (shade id == target variant id); duo/mono
      // components store a main-product variant id, so map it to the refill shade button by
      // option value (no SKU matching).
      const findShadeButton = (variantId, tab = 1) => {
        const id = String(variantId || "");
        if (!id) return null;

        const direct = candidates.find(
          (button) => button.dataset.shadeId === id || button.dataset.targetVariantId === id,
        );
        if (direct) return direct;

        const variant = state.variants.find((item) => String(item.id) === id);
        if (!variant) return null;

        const optionValue = this.normalize(variant[`option${tab}`]);
        return (
          (optionValue &&
            candidates.find(
              (button) => this.normalize(button.dataset.shadeOption || button.dataset.shadeTitle) === optionValue,
            )) ||
          null
        );
      };

      if (mode === "duo") {
        const [first, second] = ids;
        if (first) {
          state.activeTab = 1;
          const button1 = findShadeButton(first, 1);
          if (button1) this.selectShade(root, component, button1);
        }
        if (second && state.shade1) {
          state.activeTab = 2;
          const button2 = findShadeButton(second, 2);
          if (button2) this.selectShade(root, component, button2);
        }
        state.activeTab = state.shade2 ? 2 : 1;
      } else {
        const button = findShadeButton(ids[0], 1);
        if (button) this.selectShade(root, component, button);
      }

      this.hideError(component);
      this.updateComponent(root, component);
      this.updateSubmit(root);
    },

    selectShade(root, component, button) {
      const state = component.__kessSet;
      const mode = component.dataset.componentType;
      const shade = {
        id: button.dataset.shadeId,
        variantId: button.dataset.targetVariantId || button.dataset.shadeId,
        title: button.dataset.shadeTitle,
        option: button.dataset.shadeOption || button.dataset.shadeTitle,
        description: button.dataset.shadeDescription,
        image: button.dataset.shadeImage,
        swatchStyle: button.dataset.shadeSwatchStyle,
        available: button.dataset.shadeAvailable !== "false",
      };

      if (mode === "duo" && state.activeTab === 2) state.shade2 = shade;
      else state.shade1 = shade;

      state.selectedVariant = this.getSelectedComponentVariant(component);
      this.hideError(component);
      this.updateComponent(root, component);
      this.updateSubmit(root);
    },

    getSelectedComponentVariant(component) {
      const state = component.__kessSet;
      const mode = component.dataset.componentType;
      if (!state.shade1 || (mode === "duo" && !state.shade2)) return null;

      // The selected shade's variant id IS the cart id for every component (no SKU matching). For
      // perfume the shade button's availability already reflects its refill (set in Liquid), so
      // shade1.available is the sellable availability. Refill-backed duo/mono add both selected
      // refill ids in getComponentIds; here we only need shade1 for validation/availability.
      const available = state.shade1.available !== false && (mode !== "duo" || state.shade2.available !== false);
      return { id: state.shade1.variantId, available };
    },

    componentIsComplete(component) {
      if (component.dataset.requiresSelection !== "true") return true;
      const variant = component.__kessSet?.selectedVariant;
      return !!(variant?.id && variant.available !== false);
    },

    componentHasSelection(component) {
      if (component.dataset.requiresSelection !== "true") return true;
      const state = component.__kessSet;
      if (!state) return false;
      const mode = component.dataset.componentType;
      if (!state.shade1) return false;
      if (mode === "duo" && !state.shade2) return false;
      return true;
    },

    componentHasAvailableSelection(component) {
      if (component.dataset.requiresSelection !== "true") return true;
      const state = component.__kessSet;
      if (!state) return false;
      const mode = component.dataset.componentType;
      if (state.shade1?.available === false) return false;
      if (mode === "duo" && state.shade2?.available === false) return false;
      const variant = state.selectedVariant;
      return !!(variant?.id && variant.available !== false);
    },

    updateStockIndicator(root) {
      const sectionId = root.dataset.sectionId;
      if (!sectionId) return;
      const block = document.getElementById(`Inventory-${sectionId}`);
      if (!block) return;
      const components = Array.from(root.querySelectorAll("[data-kess-set-component]"));
      const allSelected = components.every((component) => this.componentHasSelection(component));
      if (!allSelected) {
        block.classList.add("kess-inventory-deferred");
        return;
      }
      const allAvailable = components.every((component) => this.componentHasAvailableSelection(component));
      block.classList.remove("kess-inventory-deferred");
      const status = block.querySelector("[data-kess-inventory-status]") || block.querySelector(".product__inventory");
      const textEl =
        block.querySelector("[data-kess-inventory-text]") || block.querySelector(".product__inventory-text");
      if (!status || !textEl) return;
      status.classList.remove(
        "product__inventory--in-stock",
        "product__inventory--low-stock",
        "product__inventory--out-of-stock",
      );
      status.classList.add(allAvailable ? "product__inventory--in-stock" : "product__inventory--out-of-stock");
      textEl.textContent = allAvailable
        ? block.dataset.kessInStockMessage || textEl.textContent
        : block.dataset.kessOutOfStockMessage || textEl.textContent;
      const bar = block.querySelector("progress-bar");
      if (bar) bar.hidden = true;
    },

    getComponentIds(component) {
      const ids = (component.dataset.fixedVariantIds || "")
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);

      const state = component.__kessSet;
      const hasRefill = component.dataset.hasRefillMapping === "true";
      const componentBundle = state?.componentBundle;
      const caseProduct = componentBundle?.caseProduct;
      const addCase = () => {
        if (componentBundle?.hasCaseProduct && caseProduct?.variantId && caseProduct?.variantAvailable) {
          ids.push(String(caseProduct.variantId));
        }
      };

      if (component.dataset.requiresSelection !== "true") {
        if (ids.length > 0) {
          if (state?.perfumeMap) {
            const entry = state.perfumeMap.map && state.perfumeMap.map[ids[0]];
            if (entry) {
              ids[0] = String(entry.id);
              if (entry.addCase !== false && state.perfumeMap.includeCase) {
                const pcase = state.perfumeMap.caseProduct;
                if (pcase && pcase.variantId && pcase.variantAvailable) ids.push(String(pcase.variantId));
              }
            } else {
              addCase();
            }
          } else {
            addCase();
          }
        }
        return ids;
      }

      const variant = state?.selectedVariant;
      if (!variant?.id) return ids;

      // Refill-backed components add the selected refill id(s) directly (duo = two ids) plus
      // the case. The component's own variant is never added.
      if (hasRefill) {
        ids.push(String(state.shade1.variantId));
        if (component.dataset.componentType === "duo" && state.shade2) ids.push(String(state.shade2.variantId));
        addCase();
        return ids;
      }

      if (state.perfumeMap) {
        const selectedId = String((state.shade1 && state.shade1.variantId) || variant.id);
        const entry = state.perfumeMap.map && state.perfumeMap.map[selectedId];
        ids.push(entry ? String(entry.id) : selectedId);
        if ((!entry || entry.addCase !== false) && state.perfumeMap.includeCase) {
          const pcase = state.perfumeMap.caseProduct;
          if (pcase && pcase.variantId && pcase.variantAvailable) ids.push(String(pcase.variantId));
        }
        return ids;
      }

      ids.push(String(variant.id));
      addCase();
      return ids;
    },

    getIncludeItemEntries(root) {
      const entries = [];
      root.querySelectorAll("[data-kess-set-component]").forEach((component) => {
        const isGift = component.dataset.isGift === "true";
        this.getComponentIds(component).forEach((id) => {
          entries.push({ id: String(id), isGift });
        });
      });
      return entries;
    },

    getIncludeItems(root) {
      return this.getIncludeItemEntries(root).map((entry) => entry.id);
    },

    getGiftItems(root) {
      return this.getIncludeItemEntries(root).map((entry) => (entry.isGift ? "true" : "false"));
    },

    getSetInSetGroups(root) {
      const groups = [];
      root.querySelectorAll("[data-kess-set-component]").forEach((component) => {
        const type = component.dataset.componentType;
        if (type !== "duo" && type !== "mono" && !component.__kessSet?.perfumeMap) return;
        const ids = this.getComponentIds(component);
        if (!ids.length) return;
        groups.push({
          name: component.dataset.productName || "",
          sku: component.dataset.productSku || "",
          ids,
        });
      });
      return groups;
    },

    validate(root, shouldScroll = true) {
      const labels = this.getLabels(root);
      let firstInvalid = null;

      root.querySelectorAll("[data-kess-set-component]").forEach((component) => {
        if (!component.__kessSet || this.componentIsComplete(component)) {
          this.hideError(component);
          return;
        }
        const state = component.__kessSet;
        const mode = component.dataset.componentType;
        let message = labels.errorShade;
        if (this.componentHasSelection(component)) {
          message = labels.errorUnavailable;
        } else if (mode === "duo") {
          message = !state.shade1 ? labels.errorFirst : labels.errorSecond;
        }
        this.showError(component, message);
        if (!firstInvalid) firstInvalid = component;
      });

      if (firstInvalid && shouldScroll)
        this.scrollTo(firstInvalid.querySelector("[data-kess-set-error]") || firstInvalid);
      this.updateSubmit(root);
      return !firstInvalid;
    },

    showError(component, message) {
      const error = component.querySelector("[data-kess-set-error]");
      if (!error) return;
      const messageEl = error.querySelector("[data-kess-set-error-message]");
      if (messageEl) messageEl.textContent = message || "";
      error.hidden = false;
    },

    hideError(component) {
      const error = component.querySelector("[data-kess-set-error]");
      if (error) error.hidden = true;
    },

    scrollTo(element) {
      if (!element) return;
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    },

    updateSubmit(root) {
      this.updateStockIndicator(root);
      const complete = Array.from(root.querySelectorAll("[data-kess-set-component]")).every((component) =>
        this.componentIsComplete(component),
      );
      this.getProductForms(root).forEach((form) => {
        const submit = form.querySelector('[name="add"]');
        if (!submit) return;
        submit.classList.toggle("kess-duo-submit--disabled", !complete);
        const label = submit.querySelector("span:first-child");
        if (label && !submit.dataset.kessSetSubmitLabel) {
          submit.dataset.kessSetSubmitLabel = label.textContent.trim();
        }
        if (complete) {
          submit.removeAttribute("aria-disabled");
          if (label)
            label.textContent = root.dataset.labelAdd || submit.dataset.kessSetSubmitLabel || label.textContent;
        } else {
          submit.setAttribute("aria-disabled", "true");
          if (label && root.dataset.labelSelectShades) label.textContent = root.dataset.labelSelectShades;
        }
      });
    },

    updateComponent(root, component) {
      const state = component.__kessSet;
      const mode = component.dataset.componentType;
      const labels = this.getLabels(root);

      const linkedVariantId = state.selectedVariant?.id || component.dataset.fixedVariantIds?.split(",")[0];
      let productUrl = component.dataset.productUrl;
      if (!linkedVariantId && component.dataset.perfumeDayUrl) {
        productUrl = state.shade1?.title?.toLowerCase().includes("night")
          ? component.dataset.perfumeNightUrl
          : component.dataset.perfumeDayUrl;
      }
      const url = new URL(productUrl, window.location.origin);
      if (mode === "duo" && state.shade1) {
        const refillIds = [state.shade1.variantId, state.shade2?.variantId].filter(Boolean);
        url.searchParams.set("variants", refillIds.join(","));
      } else if (linkedVariantId) {
        url.searchParams.set("variant", linkedVariantId);
      }
      component.querySelectorAll("[data-kess-set-product-link]").forEach((link) => {
        link.href = url.href;
      });

      component.querySelectorAll("[data-kess-set-tab]").forEach((button) => {
        const tab = Number(button.dataset.kessSetTab);
        const selected = tab === 1 ? state.shade1 : state.shade2;
        const active = tab === state.activeTab;
        button.classList.toggle("is-active", active);
        button.classList.toggle("is-selected", !!selected);
        button.classList.toggle("is-selected-inactive", !!selected && !active);
        button.setAttribute("aria-selected", active ? "true" : "false");
        if (tab === 2) button.classList.toggle("is-disabled", !state.shade1);

        const label = button.querySelector("[data-kess-set-tab-label]");
        const change = button.querySelector("[data-kess-set-change]");
        const placeholder = button.querySelector("[data-kess-set-tab-placeholder]");
        const swatchSlot = button.querySelector("[data-kess-set-tab-selected]");
        if (label) label.textContent = selected?.title || (tab === 1 ? labels.labelFirst : labels.labelSecond);
        if (change) change.hidden = !selected || active;
        if (placeholder) placeholder.hidden = !!selected;
        if (swatchSlot) {
          swatchSlot.hidden = !selected;
          swatchSlot.innerHTML = "";
          if (selected?.swatchStyle) {
            const swatch = document.createElement("span");
            swatch.className = "kess-duo-tab__media swatch-color block";
            swatch.style.cssText = selected.swatchStyle;
            swatchSlot.appendChild(swatch);
          }
        }
      });

      component.querySelectorAll("[data-kess-set-shade]").forEach((button) => {
        const selected = mode === "duo" && state.activeTab === 2 ? state.shade2 : state.shade1;
        const isSelected = selected?.id === button.dataset.shadeId;
        button.classList.toggle("is-selected", isSelected);
        button.setAttribute("aria-pressed", isSelected ? "true" : "false");
      });

      const nextButton = component.querySelector("[data-kess-set-next]");
      if (nextButton) {
        const showNext = mode === "duo" && state.activeTab !== 2 && !state.shade2;
        nextButton.hidden = !showNext;
        nextButton.classList.toggle("hidden", !showNext);
        nextButton.classList.toggle("kess-set-next--disabled", !state.shade1);
        nextButton.textContent = state.shade1 ? labels.labelNext : labels.labelSelectFirst;
      }

      this.updateComponentImage(component);
      this.updateSelectionPanel(component);
    },

    updateComponentImage(component) {
      if (component.dataset.setImageOverride === "true") return;

      const state = component.__kessSet;
      const image = component.querySelector(".kess-set-component__image img");
      if (!state || !image) return;

      if (!image.dataset.kessSetOriginalSrc) {
        image.dataset.kessSetOriginalSrc = image.getAttribute("src") || "";
        image.dataset.kessSetOriginalSrcset = image.getAttribute("srcset") || "";
        image.dataset.kessSetOriginalAlt = image.getAttribute("alt") || "";
        image.dataset.kessSetOriginalWidth = image.getAttribute("width") || "";
        image.dataset.kessSetOriginalHeight = image.getAttribute("height") || "";
      }

      // Normal (non-refill) components have a component-variant image keyed by the selected id.
      const variantImage = state.selectedVariant?.id ? state.variantImages?.[String(state.selectedVariant.id)] : null;
      if (variantImage?.src) {
        image.src = variantImage.src;
        if (variantImage.srcset) image.srcset = variantImage.srcset;
        if (variantImage.alt) image.alt = variantImage.alt;
        if (variantImage.width) image.width = variantImage.width;
        if (variantImage.height) image.height = variantImage.height;
        return;
      }

      // Refill-backed components key the cart id to the refill variant, which is absent from
      // variantImages. Use the selected shade's display image (resolved in Liquid to the matching
      // component image, else the refill image).
      const mode = component.dataset.componentType;
      const selectedShade = mode === "duo" && state.activeTab === 2 ? state.shade2 : state.shade1;
      if (selectedShade?.image) {
        image.src = selectedShade.image;
        image.removeAttribute("srcset");
        if (selectedShade.title) image.alt = selectedShade.title;
        return;
      }

      image.src = image.dataset.kessSetOriginalSrc;
      if (image.dataset.kessSetOriginalSrcset) image.srcset = image.dataset.kessSetOriginalSrcset;
      image.alt = image.dataset.kessSetOriginalAlt;
      if (image.dataset.kessSetOriginalWidth) image.width = Number(image.dataset.kessSetOriginalWidth);
      if (image.dataset.kessSetOriginalHeight) image.height = Number(image.dataset.kessSetOriginalHeight);
    },

    updateSelectionPanel(component) {
      const state = component.__kessSet;
      const selected = component.dataset.componentType === "duo" && state.activeTab === 2 ? state.shade2 : state.shade1;
      const panel = component.querySelector("[data-kess-duo-selection]");
      if (!panel) return;
      const gallery = panel.querySelector("media-gallery");
      const slides = panel.querySelector("[data-kess-duo-selection-slides]");
      const template = panel.querySelector("[data-kess-duo-selection-slide-template]");

      if (!selected) {
        panel.hidden = true;
        if (slides) slides.innerHTML = "";
        if (gallery) gallery.hidden = true;
        return;
      }

      panel.hidden = false;
      const title = panel.querySelector("[data-kess-duo-selection-title]");
      const description = panel.querySelector("[data-kess-duo-selection-description]");
      if (title) title.textContent = selected.title || "";
      if (description) description.textContent = selected.description || "";
      if (!gallery || !slides) return;
      if (gallery.__kessSetRenderedShadeId === selected.id && !gallery.hidden && slides.children.length > 0) {
        return;
      }

      slides.innerHTML = "";
      const items = state.mediaMap[selected.id] || [];
      items.forEach((item, index) => {
        const slide = this.buildSelectionGallerySlide(item, index, selected, template);
        if (slide) slides.appendChild(slide);
      });
      gallery.dataset.onlyImage = items.length === 1 ? "true" : "false";
      gallery.hidden = items.length === 0;
      gallery.__kessSetRenderedShadeId = selected.id;
      requestAnimationFrame(() => {
        if (typeof gallery.init === "function") gallery.init();
        this.ensureSelectionGallerySlider(gallery);
        this.refreshSelectionGalleryZoom(gallery);
        this.recoverSelectionGalleryHeight(gallery);
        const slider = gallery.sliderInstance && gallery.sliderInstance.slider;
        if (slider && typeof slider.update === "function") slider.update();
        requestAnimationFrame(() => this.recoverSelectionGalleryHeight(gallery));
      });
    },

    // The selection gallery element is reused for every shade, so media-gallery's own init() —
    // and with it the PhotoSwipe data source — only runs for the first selected shade. Without a
    // refresh the zoom data source keeps the previous shade's media ids, the click handler's
    // id lookup misses, and clicking an image silently stops opening the zoomed view.
    // Mirrors ensureSelectionGalleryZoom in Kess.DuoStickConfigurator.
    refreshSelectionGalleryZoom(gallery) {
      if (!gallery) return;

      gallery.__kessZoomThumbEl = null;
      // Drives whether the lightbox registers prev/next, so it must follow the new slide count.
      gallery.onlyImage = gallery.dataset.onlyImage === "true";

      if (gallery.lightbox && typeof gallery.rebuildZoomDataSource === "function") {
        gallery.rebuildZoomDataSource();
        return;
      }

      if (typeof gallery.initImageZoom === "function") {
        gallery.initImageZoom([...gallery.querySelectorAll(".product__media-item:not(.swiper-slide-duplicate)")]);
      }
    },

    buildSelectionGallerySlide(item, index, selected, template) {
      const slide = template?.content?.firstElementChild?.cloneNode(true);
      if (!slide) return null;
      const mediaId = String(item.id || `${selected.id}-${index}`);
      const zoomSrc = item.zoom_src || item.zoomSrc || item.src || selected.image || "";
      const previewSrc = item.src || item.preview || selected.image || "";
      const alt = item.alt || selected.title || "";
      const width = Number(item.width) || 1200;
      const height = Number(item.height) || 1200;

      const mediaType = item.type || "image";
      const previewHtml = item.html || item.preview_html || "";
      const zoomHtml = item.zoom_html || "";
      const isVideo = mediaType === "video" || mediaType === "external_video";

      slide.kessZoomHtml = zoomHtml;
      slide.dataset.mediaId = mediaId;
      slide.dataset.mediaType = mediaType;
      slide.dataset.mediaAlt = alt;
      slide.dataset.mediaIndex = String(index);
      slide.dataset.src = zoomSrc;
      slide.dataset.pswpWidth = String(width);
      slide.dataset.pswpHeight = String(height);

      const image = slide.querySelector("[data-kess-duo-slide-image]");
      if (image) {
        image.src = previewSrc;
        image.alt = alt;
        image.width = Math.min(width, 400);
        image.height = Math.max(1, Math.round((image.width * height) / width));

        if (isVideo && previewHtml) {
          const previewTemplate = document.createElement("template");
          previewTemplate.innerHTML = previewHtml;
          const previewElement = previewTemplate.content.firstElementChild;
          if (previewElement) image.replaceWith(previewElement);
        }
      }

      const card = slide.querySelector("[data-kess-shade-zoom-target]");
      if (card) {
        card.dataset.mediaType = mediaType;
      }

      const playIndicator = slide.querySelector("[data-kess-duo-slide-play-indicator]");
      if (playIndicator) {
        playIndicator.hidden = true;
      }

      if (isVideo && zoomHtml) {
        const zoomTemplate = document.createElement("template");
        zoomTemplate.setAttribute("data-kess-video-zoom-html", "");
        zoomTemplate.innerHTML = zoomHtml;
        slide.appendChild(zoomTemplate);
      }

      const zoomButton = slide.querySelector("[data-kess-duo-slide-zoom]");
      if (zoomButton) {
        zoomButton.dataset.mediaId = mediaId;
        zoomButton.dataset.mediaType = mediaType;
        zoomButton.dataset.mediaIndex = String(index);
      }
      const hiddenLabel = slide.querySelector("[data-kess-duo-slide-label]");
      if (hiddenLabel) hiddenLabel.textContent = `Open media ${index + 1}`;
      return slide;
    },

    recoverSelectionGalleryHeight(gallery) {
      const wrapper = gallery?.querySelector("[data-kess-duo-selection-slides]");
      if (!wrapper) return;
      wrapper.style.height = "";
      if (wrapper.getBoundingClientRect().height > 0) return;
      const card = wrapper.querySelector(".kess-shade-gallery__card");
      const cardHeight = card ? Math.ceil(card.getBoundingClientRect().height) : 0;
      if (cardHeight > 0) {
        wrapper.style.height = `${cardHeight}px`;
      }
    },

    ensureSelectionGallerySlider(gallery) {
      if (!gallery || !window.FoxTheme?.Carousel) return;
      const viewer = gallery.querySelector('[id^="GalleryViewer"]');
      if (!viewer || viewer.classList.contains("swiper-initialized") || gallery.sliderInstance?.slider) return;
      const gap = parseInt(gallery.dataset.mediaItemGap || "0", 10);
      gallery.sliderInstance = new window.FoxTheme.Carousel(viewer, {
        init: false,
        slidesPerView: "auto",
        spaceBetween: Number.isNaN(gap) ? 0 : gap,
        loop: false,
        grabCursor: true,
        allowTouchMove: true,
        autoHeight: false,
        threshold: 2,
      });
      gallery.sliderInstance.init();
      if (!gallery.sliderInstance.slider) {
        gallery.sliderInstance = false;
        return;
      }
      gallery.sliderInstance.slider.init();
    },
  };

  Kess.Bundles.register("set-components", {
    selector: "[data-kess-set-bundle-data]",
    preventSubmitOnInvalid: true,
    getRoot() {
      const section = this.element.closest(".shopify-section") || document;
      return Kess.SetComponents.getRoot(section);
    },
    validate() {
      const root = this.handler.getRoot.call(this);
      return root ? Kess.SetComponents.validate(root, true) : false;
    },
    getBundleName() {
      return this.data.bundleName || "";
    },
    getGlobalSetId() {
      return this.data.globalSetId || "";
    },
    getIncludeItems() {
      const root = this.handler.getRoot.call(this);
      return root ? Kess.SetComponents.getIncludeItems(root) : [];
    },
    getProperties(includeItems = []) {
      const root = this.handler.getRoot.call(this);
      const giftItems = root ? Kess.SetComponents.getGiftItems(root) : includeItems.map(() => "false");
      const properties = {
        _giftItems: giftItems.join(","),
        _itemcategory: "set",
      };
      const groups = root ? Kess.SetComponents.getSetInSetGroups(root) : [];
      if (groups.length) {
        properties._setInSetItems = groups.map((g) => g.ids.join(",")).join("|");
        properties._setInSetNames = groups.map((g) => g.name).join(",");
        properties._setInSetSkus = groups.map((g) => g.sku).join(",");
      }
      return properties;
    },
  });

  Kess.GeolocationPopup = {
    init(container = document) {
      const selectors = {
        root: "[data-kess-geolocation-popup]",
        content: "[data-kess-geolocation-content]",
        close: "[data-kess-geolocation-close]",
        redirect: ".kess-geolocation-popup__redirect",
      };
      const storageKeys = {
        country: "kess:userCountryCode",
        countryTimestamp: "kess:userCountryCodeTimestamp",
        dismissed: "kess:geolocationPopupDismissed",
      };
      const hasStorage = () => {
        try {
          window.localStorage.setItem("kess:storage-test", "1");
          window.localStorage.removeItem("kess:storage-test");
          return true;
        } catch (error) {
          return false;
        }
      };

      const storageAvailable = hasStorage();

      const getStoredValue = (key) => {
        if (!storageAvailable) return null;
        try {
          return window.localStorage.getItem(key);
        } catch (error) {
          return null;
        }
      };

      const setStoredValue = (key, value) => {
        if (!storageAvailable) return;
        try {
          window.localStorage.setItem(key, value);
        } catch (error) {}
      };

      const normalizeCountry = (country) =>
        String(country || "")
          .trim()
          .toUpperCase();

      const dispatchCountry = (country) => {
        const normalizedCountry = normalizeCountry(country);
        document.documentElement.dataset.country = normalizedCountry;
        document.body.dataset.country = normalizedCountry;
        window.dispatchEvent(new CustomEvent("userLocationDetected", { detail: normalizedCountry }));
      };

      const detectCountry = async () => {
        const storedCountry = getStoredValue(storageKeys.country);
        if (storedCountry) {
          dispatchCountry(storedCountry);
          return storedCountry;
        }

        try {
          const response = await fetch("/browsing_context_suggestions.json", { credentials: "same-origin" });
          if (!response.ok) throw new Error("Location request failed");
          const data = await response.json();
          const country = normalizeCountry(data?.detected_values?.country?.handle);
          if (!country) throw new Error("Missing country");
          setStoredValue(storageKeys.country, country);
          setStoredValue(storageKeys.countryTimestamp, new Date().toISOString());
          dispatchCountry(country);
          return country;
        } catch (error) {
          dispatchCountry("DEFAULT");
          return "DEFAULT";
        }
      };

      const isCurrentMarket = (targetUrl) => {
        try {
          const target = new URL(targetUrl, window.location.origin);
          if (target.origin !== window.location.origin) return false;
          const targetPath = target.pathname.replace(/\/$/, "") || "/";
          const currentPath = window.location.pathname.replace(/\/$/, "") || "/";
          return targetPath === "/" || currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
        } catch (error) {
          return false;
        }
      };

      const closePopup = (root) => {
        if (typeof root.hide === "function") {
          root.hide();
        } else {
          root.hidden = true;
          root.removeAttribute("open");
        }
        setStoredValue(storageKeys.dismissed, "true");
      };

      const openPopup = (root, content) => {
        root.querySelectorAll(selectors.content).forEach((item) => {
          item.hidden = item !== content;
        });
        const title = content.querySelector(".kess-geolocation-popup__heading");
        if (title) root.setAttribute("aria-labelledby", title.id);
        setStoredValue(storageKeys.dismissed, "true");
        if (typeof root.show === "function") {
          root.show(null, false);
        } else {
          root.hidden = false;
          root.setAttribute("open", "immediate");
        }
      };

      const initPopup = async (root) => {
        if (!root || root.dataset.kessGeolocationInitialized === "true") return;
        root.dataset.kessGeolocationInitialized = "true";

        root.addEventListener("click", (event) => {
          if (!event.target.closest(selectors.close)) return;
          event.preventDefault();
          closePopup(root);
        });

        if (getStoredValue(storageKeys.dismissed)) return;

        const country = normalizeCountry(await detectCountry());
        const matchingContent = Array.from(root.querySelectorAll(selectors.content)).find((content) => {
          const countries = String(content.dataset.countryCodes || "")
            .split(",")
            .map(normalizeCountry);
          return (
            countries.includes(country) && content.dataset.locationUrl && !isCurrentMarket(content.dataset.locationUrl)
          );
        });

        if (matchingContent) {
          window.setTimeout(() => openPopup(root, matchingContent), 500);
        }
      };

      const init = (container = document) => {
        container.querySelectorAll(selectors.root).forEach(initPopup);
      };

      init(container);
    },
  };

  /**
   * ============================================================================
   * CART DRAWER ENHANCEMENTS (Trello 534 - Relaunch Cart)
   * - Forces the discount code input to uppercase (matches the OACE cart, where
   *   the input is always in capital letters).
   * - Mirrors the free-shipping "goal reached" state onto the cart drawer so the
   *   footer can swap the shipping cost for the "Kostenlos" label currency-safely.
   * ============================================================================
   */
  Kess.CartDrawerEnhancements = {
    selectors: {
      discountInput: "[data-kess-discount-input]",
      freeShippingGoal: "free-shipping-goal",
      discountChips: "[data-kess-cart-discount-chips]",
      freeShippingChipTemplate: "[data-kess-free-shipping-chip-template]",
      bestsellersSlider: "[data-kess-bestsellers-slider]",
    },
    reachedClass: "cart--free-shipping-reached",
    shippingCodeClass: "cart--free-shipping-code-applied",
    doneClass: "free-shipping-goal--done",

    init() {
      this.bindUppercaseDiscount();
      this.bindFreeShippingMirror();
      this.bindFreeShippingDiscountCodes();
      this.bindBestsellersSlider();
    },

    bindUppercaseDiscount() {
      // Delegated on document so it survives footer re-renders.
      document.addEventListener("input", (event) => {
        const input = event.target.closest && event.target.closest(this.selectors.discountInput);
        if (!input) return;
        const upper = input.value.toUpperCase();
        if (upper === input.value) return;
        const start = input.selectionStart;
        const end = input.selectionEnd;
        input.value = upper;
        try {
          input.setSelectionRange(start, end);
        } catch (error) {
          /* unsupported input type */
        }
      });
    },

    bindFreeShippingMirror() {
      // The footer re-renders on every cart update, but the "reached" state is a
      // class on <cart-drawer> (outside the re-rendered region), so the footer's
      // cost ↔ "Kostenlos" swap needs the class kept in sync. Use the fresh cart
      // payload when available instead of relying on the free-shipping-goal class
      // timing; otherwise removing items below the threshold can leave the drawer
      // in a stale "reached" state and hide the shipping cost.
      this.syncFreeShipping();
      // custom.js (head) executes before cart.js (section script), so on page
      // load the goal element isn't upgraded yet — re-sync once it is.
      if (window.customElements?.whenDefined) {
        customElements.whenDefined(this.selectors.freeShippingGoal).then(() => this.syncFreeShipping());
      }
      document.addEventListener("cart:updated", (event) => this.syncFreeShipping(event.detail?.cart));
    },

    syncFreeShipping(cart = null) {
      document.querySelectorAll(this.selectors.freeShippingGoal).forEach((goal) => {
        const container = goal.closest("cart-drawer") || goal.closest(".cart");
        if (!container) return;

        // The Sleek 2.3.0 element resolves its per-currency config into the
        // `goalMajor` property (data-minimum-amount no longer exists) — read
        // that so mirror and progress bar share one threshold. Until the
        // element is upgraded, goalMajor doesn't exist — leave the
        // server-rendered class untouched instead of guessing.
        const minimumAmount = Number(goal.goalMajor);
        if (!Number.isFinite(minimumAmount) || minimumAmount <= 0) return;

        const cartTotal = goal.getCartTotal(cart);
        if (!Number.isFinite(cartTotal)) return;

        container.classList.toggle(this.reachedClass, cartTotal / 100 >= minimumAmount);
      });
    },

    bindFreeShippingDiscountCodes() {
      this.loadCart().then((cart) => this.syncFreeShippingDiscountCodes(cart));
      document.addEventListener("cart:updated", (event) => this.syncFreeShippingDiscountCodes(event.detail?.cart));
    },

    async loadCart() {
      try {
        const response = await fetch(FoxTheme.routes.cart_url, FoxTheme.utils.fetchConfig("json", "GET"));
        return response.ok ? await response.json() : null;
      } catch (_) {
        return null;
      }
    },

    getFreeShippingDiscountCodes(cart) {
      const appliedCodes = Array.isArray(cart?.discount_codes)
        ? cart.discount_codes.filter((discount) => discount?.applicable && discount.code)
        : [];
      const allocatedCodes = new Set(
        [
          ...(cart?.cart_level_discount_applications || []),
          ...(cart?.items || []).flatMap((item) => item.line_level_discount_allocations || []),
        ]
          .map((discount) => discount.discount_application || discount)
          .filter((discount) => discount?.type === "discount_code" && discount.title)
          .map((discount) => String(discount.title).toUpperCase()),
      );

      return appliedCodes
        .map((discount) => String(discount.code))
        .filter((code) => !allocatedCodes.has(code.toUpperCase()));
    },

    syncFreeShippingDiscountCodes(cart) {
      const codes = this.getFreeShippingDiscountCodes(cart);
      document.querySelectorAll(this.selectors.freeShippingGoal).forEach((goal) => {
        goal.freeShippingCodeApplied = codes.length > 0;
        const total = goal.getCartTotal?.(cart);
        if (Number.isFinite(total)) goal.updateShippingGoal(total);
      });
      document.querySelectorAll("cart-drawer").forEach((drawer) => {
        drawer.classList.toggle(this.shippingCodeClass, codes.length > 0);
        this.renderFreeShippingDiscountCodes(drawer, codes);
      });
    },

    renderFreeShippingDiscountCodes(drawer, codes) {
      const list = drawer.querySelector(this.selectors.discountChips);
      const template = drawer.querySelector(this.selectors.freeShippingChipTemplate);
      if (!list || !template) return;

      list.querySelectorAll("[data-kess-free-shipping-discount]").forEach((item) => item.remove());
      codes.forEach((code) => {
        const chip = template.content.firstElementChild.cloneNode(true);
        chip.dataset.discountCode = code;
        chip.querySelector("[data-kess-free-shipping-code]").textContent = code;
        const removeButton = chip.querySelector("[data-kess-free-shipping-remove]");
        removeButton.setAttribute("aria-label", removeButton.getAttribute("aria-label").replace("__code__", code));
        list.append(chip);
      });
      list.classList.toggle("hidden", list.querySelectorAll("[data-discount-code]").length === 0);
    },

    bindBestsellersSlider() {
      // The empty-cart bestsellers use the theme's Swiper (grab/drag), like the
      // PDP galleries. Swiper needs a visible, measured container, so we init when
      // the drawer finishes opening (drawer:handleAfterShow) and after the empty
      // state re-renders (cart:updated). Guarded so each slider inits only once.
      const init = () => this.initBestsellersSliders();
      document.addEventListener("drawer:handleAfterShow", init);
      document.addEventListener("cart:updated", init);
    },

    initBestsellersSliders() {
      if (!window.FoxTheme || !window.FoxTheme.Carousel) return;
      document.querySelectorAll(this.selectors.bestsellersSlider).forEach((el) => {
        if (el.classList.contains("swiper-initialized")) return;
        if (el.offsetParent === null) return; // still hidden — wait until visible
        const carousel = new window.FoxTheme.Carousel(el, {
          slidesPerView: "auto",
          spaceBetween: 10,
          grabCursor: true,
          allowTouchMove: true,
          threshold: 2,
        });
        carousel.init();
      });
    },
  };

  Kess.CartUpsellRules = {
    initialized: false,
    isClearing: false,

    init() {
      if (this.initialized) return;
      this.initialized = true;

      window.FoxTheme?.pubsub?.subscribe?.(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
        this.cleanupFromCart(event?.cart);
      });
      document.addEventListener("cart:updated", () => this.cleanupFromDom());
      document.addEventListener("product-ajax:added", () => this.cleanupFromDom());
      this.cleanupFromDom();
    },

    isUpsellItem(item) {
      const properties = item?.properties || {};
      return properties._upsell_product;
    },

    getOnlyUpsellKeys(cart) {
      const items = cart?.items || [];
      if (!items.length) return [];

      let hasRegularPhysicalItem = false;
      const upsellKeys = [];

      items.forEach((item) => {
        if (this.isUpsellItem(item)) {
          if (item.key) upsellKeys.push(item.key);
        } else if (!item.gift_card) {
          // Not requires_shipping: bundle shell parents (duo/mono sticks, sets)
          // are non-shipping variants whose physical goods live on their Cart
          // Transform components, which /cart.js doesn't expose. Only
          // gift cards are classified client-side; the digital-collection case
          // stays server-side in custom-cart-upsell.liquid's
          // data-kess-cart-upsell-only-items script.
          hasRegularPhysicalItem = true;
        }
      });

      return hasRegularPhysicalItem ? [] : upsellKeys;
    },

    cleanupFromCart(cart) {
      const keys = this.getOnlyUpsellKeys(cart);
      if (keys.length) this.removeByKeys(keys);
    },

    cleanupFromDom() {
      document.querySelectorAll("[data-kess-cart-upsell-only-items]").forEach((script) => {
        try {
          const keys = JSON.parse(script.textContent || "[]");
          script.remove();
          if (Array.isArray(keys) && keys.length) this.removeByKeys(keys);
        } catch (error) {
          script.remove();
        }
      });
    },

    async removeByKeys(keys) {
      if (this.isClearing || !keys.length) return;
      this.isClearing = true;

      try {
        const sections = [];
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:grouped-sections", { bubbles: true, detail: { sections } }),
        );

        const updates = keys.reduce((acc, key) => {
          acc[key] = 0;
          return acc;
        }, {});
        const ensureJsExtension = (url) => (url?.endsWith(".js") ? url : `${url}.js`);
        const updateUrl = ensureJsExtension(window.FoxTheme?.routes?.cart_update_url || "/cart/update");
        const response = await fetch(updateUrl, {
          ...(window.FoxTheme?.utils?.fetchConfig?.("javascript") || {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/javascript" },
          }),
          body: JSON.stringify({
            updates,
            sections,
            sections_url: window.location.pathname,
          }),
        });
        const cartState = await response.json();
        if (!response.ok || cartState.status) return;

        window.FoxTheme?.pubsub?.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartState });
      } finally {
        this.isClearing = false;
      }
    },
  };

  if (!customElements.get("cart-upsell")) {
    customElements.define(
      "cart-upsell",
      class CartUpsell extends HTMLElement {
        connectedCallback() {
          this.addEventListener("change", this.handleChange.bind(this));
          this.addEventListener("submit", this.handleSubmit.bind(this));
        }

        handleChange(event) {
          const input = event.target.closest("[data-kess-cart-upsell-variant]");
          if (!input) return;

          const item = input.closest("[data-kess-cart-upsell-item]");
          if (!item) return;

          const idInput = item.querySelector("[data-kess-cart-upsell-id]");
          const button = item.querySelector(".kess-cart-upsell__button");
          const finalPrice = item.querySelector("[data-kess-cart-upsell-final-price]");
          const priceWrap = item.querySelector("[data-kess-cart-upsell-price]");
          const shade = item.querySelector("[data-kess-cart-upsell-shade]");
          const size = item.querySelector("[data-kess-cart-upsell-size]");
          const unit = item.querySelector("[data-kess-cart-upsell-unit]");
          const unitPrice = item.querySelector("[data-kess-cart-upsell-unit-price]");
          const description = item.querySelector("[data-kess-cart-upsell-description]");
          const image = item.querySelector(".kess-cart-upsell__media img");

          if (idInput) idInput.value = input.value;
          if (button) button.disabled = input.dataset.available !== "true";
          if (finalPrice) finalPrice.textContent = input.dataset.price || "";
          if (image && input.dataset.imageSrc) {
            image.src = input.dataset.imageSrc;
            image.srcset = input.dataset.imageSrcset || "";
            image.alt = input.dataset.imageAlt || image.alt;
          }

          if (priceWrap) {
            const compare = priceWrap.querySelector(".kess-cart-upsell__compare");
            if (compare) {
              compare.textContent = input.dataset.comparePrice || "";
              compare.hidden = !input.dataset.comparePrice;
            }
          }

          this.updateTextNode(shade, input.dataset.shade);
          this.updateTextNode(size, input.dataset.size);
          this.updateTextNode(unit, input.dataset.unit);
          this.updateTextNode(unitPrice, input.dataset.unitPrice);
          this.updateTextNode(description, input.dataset.description);
        }

        updateTextNode(element, value) {
          if (!element) return;
          element.textContent = value || "";
          element.hidden = !value;
        }

        async handleSubmit(event) {
          const form = event.target.closest("[data-kess-cart-upsell-form]");
          if (!form) return;
          event.preventDefault();

          const button = form.querySelector("button[type='submit']");
          const error = form.querySelector("[data-kess-cart-upsell-error]");
          const id = form.querySelector("[data-kess-cart-upsell-id]")?.value;
          if (!id) return;

          if (error) {
            error.hidden = true;
            error.textContent = "";
          }
          if (button) button.classList.add("btn--loading");

          try {
            const sections = [];
            document.documentElement.dispatchEvent(
              new CustomEvent("cart:grouped-sections", { bubbles: true, detail: { sections } }),
            );

            const referenceProduct = form.dataset.referenceProduct || "";
            const referenceProductKey = form.dataset.referenceProductKey || "";

            const body = JSON.stringify({
              items: [
                {
                  id,
                  quantity: 1,
                  properties: {
                    // The upsell discount amount is applied server-side by the
                    // upsell-discount Function (from the product metafield), so
                    // no discount value is sent from the client here — only the
                    // marker that flags this line as upsell-added.
                    _upsell_product: `${id};`,
                    _upsell_ref_product: referenceProduct ? `${referenceProduct};` : "",
                    _upsell_reference_product_key: referenceProductKey ? `${referenceProductKey};` : "",
                    _itemcategory: "singleproduct;",
                  },
                },
              ],
              sections,
              sections_url: window.location.pathname,
            });
            const ensureJsExtension = (url) => (url?.endsWith(".js") ? url : `${url}.js`);

            const addResponse = await fetch(ensureJsExtension(window.FoxTheme?.routes?.cart_add_url || "/cart/add"), {
              ...(window.FoxTheme?.utils?.fetchConfig?.("javascript") || {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/javascript" },
              }),
              body,
            });
            const addState = await addResponse.json();

            if (!addResponse.ok || addState.status) {
              throw new Error(addState.description || addState.message || "Error");
            }

            const cartResponse = await fetch(ensureJsExtension(window.FoxTheme?.routes?.cart_url || "/cart"), {
              ...(window.FoxTheme?.utils?.fetchConfig?.("json", "GET") || {
                method: "GET",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
              }),
            });
            const cartState = await cartResponse.json();
            cartState.sections = addState.sections;

            window.FoxTheme?.pubsub?.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartState });
            document.dispatchEvent(new CustomEvent("product-ajax:added", { detail: { product: addState } }));
          } catch (caughtError) {
            if (error) {
              error.textContent = caughtError.message;
              error.hidden = false;
            }
          } finally {
            if (button) button.classList.remove("btn--loading");
          }
        }
      },
    );
  }

  /**
   * ============================================================================
   * KESS B2B MINIMUM ORDER VALUE
   * ============================================================================
   */
  if (!customElements.get("kess-min-order-value")) {
    customElements.define(
      "kess-min-order-value",
      class KessMinOrderValue extends HTMLElement {
        connectedCallback() {
          const amount = Number(this.dataset.shopAmount);
          const rate = Number(window.Shopify?.currency?.rate);
          const currency = (window.Shopify?.currency?.active || this.dataset.marketCurrency || "").toUpperCase();
          if (!Number.isFinite(amount) || amount <= 0) return;
          if (!Number.isFinite(rate) || rate <= 0 || !currency) return;

          // A display conversion only — a market's manual rate and price adjustments
          // are not exposed to the theme. Round up rather than understate a minimum.
          const converted = Math.ceil(amount * rate);
          try {
            this.textContent = new Intl.NumberFormat(document.documentElement.lang || undefined, {
              style: "currency",
              currency,
              maximumFractionDigits: 0,
            }).format(converted);
          } catch (error) {
            // Unsupported currency code — keep the server-rendered fallback.
          }
        }
      },
    );
  }

  /**
   * ============================================================================
   * KESS B2B BUY CONTROLS
   * ============================================================================
   */
  Kess.B2BBuyControls = {
    upsellBundles: new WeakMap(),
    selectors: {
      scope: "[data-kess-b2b-scope]",
      swatch: "[data-kess-b2b-swatch]",
      shadeInfo: "[data-kess-b2b-shade-info]",
      shadeName: "[data-kess-b2b-shade-name]",
      shadeDescription: "[data-kess-b2b-shade-description]",
      quantity: "[data-kess-b2b-quantity]",
      atc: "[data-kess-b2b-atc]",
      variantId: "[data-kess-b2b-variant-id]",
      priceRow: "[data-kess-b2b-price-row]",
    },

    init() {
      if (this.ready) return;
      this.ready = true;

      document.addEventListener("click", this.handleClick.bind(this));
      document.addEventListener("submit", this.handleUpsellSubmit.bind(this), true);
      document.addEventListener("change", this.handleQuantityChange.bind(this));
      document.addEventListener("input", this.handleQuantityChange.bind(this));
      document.addEventListener("pointerover", this.handleTooltip.bind(this));
      document.addEventListener("focusin", this.handleTooltip.bind(this));

      document.addEventListener("shopify:section:load", (event) => this.syncAll(event.target));

      const pubsub = window.FoxTheme?.pubsub;
      if (pubsub?.PUB_SUB_EVENTS?.variantChange) {
        pubsub.subscribe(pubsub.PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));
      }

      this.syncAll();
    },

    /**
     * @param {ParentNode} root - Subtree to sync
     */
    syncAll(root = document) {
      if (!root || typeof root.querySelectorAll !== "function") return;
      root.querySelectorAll(this.selectors.atc).forEach((button) => this.syncButton(button));
      root.querySelectorAll(this.selectors.quantity).forEach((input) => this.syncQuantity(input));
    },

    handleClick(event) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const swatch = event.target.closest?.(this.selectors.swatch);
      if (!swatch) return;

      const scope = swatch.closest(this.selectors.scope);
      if (!scope) return;

      event.preventDefault();
      this.selectSwatch(scope, swatch);
    },

    /**
     * @param {HTMLElement} scope - The card the swatch belongs to
     * @param {HTMLElement} swatch - The clicked swatch
     */
    selectSwatch(scope, swatch) {
      scope.querySelectorAll(this.selectors.swatch).forEach((item) => {
        item.setAttribute("aria-current", item === swatch ? "true" : "false");
      });

      const info = scope.querySelector(this.selectors.shadeInfo);
      const name = scope.querySelector(this.selectors.shadeName);
      const description = scope.querySelector(this.selectors.shadeDescription);
      const nameText = swatch.dataset.kessB2bSwatchName || "";
      const descriptionText = swatch.dataset.kessB2bSwatchDescription || "";

      const hasText = nameText !== "" || descriptionText !== "";
      if (info) info.classList.toggle("hidden", !hasText);
      if (name) name.textContent = nameText;
      if (description) description.textContent = descriptionText;

      const variantInput = scope.querySelector(this.selectors.variantId);
      if (variantInput) variantInput.value = swatch.dataset.kessB2bSwatchVariant || "";

      this.applySwatchPrice(scope, swatch);
      this.applySwatchUnitPrice(scope, swatch);

      const soldOut = swatch.dataset.kessB2bSwatchAvailable === "false" || !this.upsellRefillAvailable(scope);
      const quantityInput = scope.querySelector(this.selectors.quantity);
      this.applyVariantRule(quantityInput, {
        min: swatch.dataset.kessB2bSwatchQtyMin,
        increment: swatch.dataset.kessB2bSwatchQtyIncrement,
        max: swatch.dataset.kessB2bSwatchQtyMax,
      });
      // The stepper only re-checks its buttons on its own input events, so a plus button disabled
      // at the previous shade's maximum would stay disabled under a higher one.
      quantityInput?.closest("quantity-input")?.validateQtyRules?.();

      scope.querySelectorAll(this.selectors.atc).forEach((button) => {
        this.setSoldOut(button, soldOut);
        this.syncButton(button);
      });
    },

    /**
     * Keeps a shade-name bubble inside the card it belongs to. The card hides its overflow for
     * its rounded corners, so a bubble centred on a swatch near either edge would be cut off;
     * this offsets it back inside and leaves its arrow over the swatch.
     *
     * @param {Event} event - A pointerover or focusin event
     */
    handleTooltip(event) {
      const item = event.target.closest?.("[data-tooltip]");
      if (!item) return;

      const card = item.closest(".kess-upsell-card");
      const tooltip = item.querySelector(".tooltip");
      if (!card || !tooltip) return;

      // Measured from layout geometry, not the rendered box: the bubble carries a scale
      // transform that is still animating to full size while this runs.
      const bounds = card.getBoundingClientRect();
      const anchor = item.getBoundingClientRect();
      const half = tooltip.offsetWidth / 2;
      const centre = anchor.left + anchor.width / 2;
      const inset = 8;

      let shift = 0;
      if (centre - half < bounds.left + inset) shift = bounds.left + inset - (centre - half);
      else if (centre + half > bounds.right - inset) shift = bounds.right - inset - (centre + half);

      if (shift) tooltip.style.setProperty("--kess-tooltip-shift", `${Math.round(shift)}px`);
      else tooltip.style.removeProperty("--kess-tooltip-shift");
    },

    /**
     * Shows the selected shade's own price, for cards whose shades are not priced alike.
     * Without this the card would keep the default shade's figure while submitting another
     * variant, advertising one price and adding a different one.
     *
     * @param {HTMLElement} scope - The card the swatch belongs to
     * @param {HTMLElement} swatch - The selected swatch
     */
    applySwatchPrice(scope, swatch) {
      const row = scope.querySelector(this.selectors.priceRow);
      const price = swatch.dataset.kessB2bSwatchPrice;
      if (!row || !price) return;

      // A product with quantity price breaks renders a price RANGE, and a second regular item
      // holding the struck compare-at. Rewriting either would collapse the range to one figure or
      // overwrite the strike, so those rows keep whatever the server rendered.
      if (row.hasAttribute("data-kess-b2b-volume-pricing")) return;

      const compareAt = swatch.dataset.kessB2bSwatchCompareAt;
      const regular = row.querySelector(".f-price__regular .f-price-item--regular");
      const sale = row.querySelector(".f-price__sale .f-price-item--sale");
      if (regular) regular.textContent = price;
      if (sale) sale.textContent = price;

      const struck = row.querySelector(".f-price__sale .f-price-item--regular s");
      if (struck) struck.textContent = compareAt || "";
      const wrapper = row.querySelector(".f-price");
      if (wrapper) wrapper.classList.toggle("f-price--on-sale", Boolean(compareAt));
    },

    /**
     * Shows the selected shade's own unit price. Shades of one size can still be priced
     * differently, and each carries its unit price already formatted by the server.
     *
     * @param {HTMLElement} scope - The card the swatch belongs to
     * @param {HTMLElement} swatch - The selected swatch
     */
    applySwatchUnitPrice(scope, swatch) {
      const row = scope.querySelector(this.selectors.priceRow);
      const wrapper = row?.querySelector(".f-price__unit-wrapper");
      if (!wrapper || row.hasAttribute("data-kess-b2b-volume-pricing")) return;

      const unitPrice = swatch.dataset.kessB2bSwatchUnitPrice || "";
      wrapper.classList.toggle("hidden", unitPrice === "");
      if (unitPrice === "") return;

      const figure = wrapper.querySelector("[data-unit-price]");
      const base = wrapper.querySelector("[data-unit-price-base-unit]");
      const measure = wrapper.querySelector(".f-price__unit")?.firstChild;
      if (figure) figure.textContent = unitPrice;
      if (base) base.textContent = swatch.dataset.kessB2bSwatchUnitBase || "";
      if (measure?.nodeType === Node.TEXT_NODE) {
        measure.textContent = `${swatch.dataset.kessB2bSwatchUnitMeasure || ""} (`;
      }
    },

    /**
     * @param {HTMLInputElement|null} input - The stepper's number input
     * @param {{min?: string, increment?: string, max?: string}} rule - The variant's quantity rule
     */
    applyVariantRule(input, rule) {
      if (!input || !rule) return;

      if (rule.min) input.dataset.kessB2bQtyMin = rule.min;
      if (rule.increment) {
        input.dataset.kessB2bQtyIncrement = rule.increment;
        input.setAttribute("step", rule.increment);
      }
      if (rule.max) {
        input.dataset.kessB2bQtyMax = rule.max;
        input.setAttribute("max", rule.max);
      } else {
        delete input.dataset.kessB2bQtyMax;
        input.removeAttribute("max");
      }
    },

    /**
     * @param {HTMLElement} button - A quantity-gated add-to-cart button
     * @param {boolean} soldOut - Whether the selected variant is unavailable
     */
    setSoldOut(button, soldOut) {
      const label = button.querySelector("span");

      if (soldOut) {
        button.dataset.kessB2bSoldout = "true";
        button.setAttribute("disabled", "disabled");
        const soldOutText = this.decode(window.FoxTheme?.variantStrings?.soldOut);
        if (label && soldOutText) label.textContent = soldOutText;
        return;
      }

      if (button.dataset.kessB2bSoldout !== "true") return;
      delete button.dataset.kessB2bSoldout;
      const addToCart = button.dataset.kessB2bAtcLabel || this.decode(window.FoxTheme?.variantStrings?.addToCart);
      if (label && addToCart) label.textContent = addToCart;
    },

    handleQuantityChange(event) {
      const input = event.target.closest?.(this.selectors.quantity);
      if (!input) return;

      this.syncQuantity(input);

      const scope = input.closest("form") || input.closest(this.selectors.scope);
      if (!scope) return;
      scope.querySelectorAll(this.selectors.atc).forEach((button) => this.syncButton(button));
    },

    /**
     * @param {HTMLInputElement|null} input - The stepper's number input, if the form has one
     * @returns {number}
     */
    quantityValue(input) {
      return input ? parseInt(input.value, 10) || 0 : 1;
    },

    /**
     * @param {HTMLInputElement|null} input - The stepper's number input
     * @returns {{min: number, increment: number, max: number|null}}
     */
    quantityRule(input) {
      const data = input?.dataset || {};
      const min = parseInt(data.kessB2bQtyMin, 10);
      const increment = parseInt(data.kessB2bQtyIncrement, 10);
      const max = parseInt(data.kessB2bQtyMax, 10);
      return {
        min: min > 0 ? min : 1,
        increment: increment > 0 ? increment : 1,
        max: max > 0 ? max : null,
      };
    },

    /**
     * @param {HTMLInputElement|null} input - The stepper's number input
     * @returns {boolean}
     */
    quantityMeetsRule(input) {
      const quantity = this.quantityValue(input);
      if (quantity <= 0) return false;
      if (!input) return true;

      const rule = this.quantityRule(input);
      if (quantity < rule.min) return false;
      if (rule.max !== null && quantity > rule.max) return false;
      return (quantity - rule.min) % rule.increment === 0;
    },

    /**
     * @param {HTMLInputElement} input - The stepper's number input
     */
    syncQuantity(input) {
      if (!input) return;
      const stepper = input.closest("quantity-input") || input.closest(".quantity");
      if (!stepper) return;
      stepper.classList.toggle("kess-b2b-qty--active", this.quantityValue(input) > 0);
    },

    getUpsellBundle(scope) {
      const element = scope?.querySelector("[data-kess-upsell-bundle-data]");
      if (!element) return null;
      let instance = this.upsellBundles.get(element);
      if (!instance) {
        instance = {
          element,
          data: Kess.Bundles.parseData(element),
          handler: Kess.Bundles.handlers["product-bundle"],
        };
        this.upsellBundles.set(element, instance);
      }
      instance.currentVariantId = scope.querySelector(this.selectors.variantId)?.value;
      return instance;
    },

    upsellRefillAvailable(scope) {
      const instance = this.getUpsellBundle(scope);
      if (!instance) return true;
      if (!instance.data) return false;
      const refill =
        instance.handler.getProductRefill.call(instance) || instance.handler.getPerfumeRefill.call(instance);
      return refill?.available !== false;
    },

    handleUpsellSubmit(event) {
      const form = event.target;
      const scope = form.closest?.(this.selectors.scope);
      const instance = this.getUpsellBundle(scope);
      if (!instance) return;
      const button = form.querySelector(this.selectors.atc);
      if (!button) return;
      this.syncButton(button);
      if (button.disabled || button.getAttribute("aria-disabled") === "true") {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      const includeItems = instance.handler.getIncludeItems.call(instance);
      form.querySelectorAll("[data-kess-upsell-property]").forEach((input) => input.remove());
      const category = form.querySelector('[name="properties[_itemcategory]"]');
      if (category) category.disabled = includeItems.length > 0;
      if (!includeItems.length) return;

      const properties = {
        _includeItem: includeItems.join(","),
        _setId: Date.now().toString(),
        _globalSetId: instance.handler.getGlobalSetId.call(instance),
        _set_name: instance.handler.getBundleName.call(instance),
      };
      Object.entries(properties).forEach(([name, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = `properties[${name}]`;
        input.value = value;
        input.dataset.kessUpsellProperty = "";
        form.appendChild(input);
      });
    },

    /**
     * @param {HTMLElement} button - A quantity-gated add-to-cart button
     */
    syncButton(button) {
      if (!this.upsellRefillAvailable(button.closest(this.selectors.scope))) {
        this.setSoldOut(button, true);
        return;
      }
      if (button.dataset.kessB2bSoldout === "true") return;

      const scope = button.closest("form") || button.closest(this.selectors.scope);
      const quantityInput = scope?.querySelector(this.selectors.quantity);

      let ready = this.quantityMeetsRule(quantityInput);
      if (button.dataset.kessB2bRequiresSwatch === "true") {
        const variantInput = scope?.querySelector(this.selectors.variantId);
        if (!variantInput || !variantInput.value) ready = false;
      }

      button.toggleAttribute("disabled", !ready);
    },

    handleVariantChange(event) {
      const sectionId = event?.data?.sectionId;
      const variant = event?.data?.variant;
      if (!sectionId) return;

      const form = document.getElementById(`product-form-${sectionId}`);
      const button = form?.querySelector(this.selectors.atc);
      if (!button) return;

      if (!variant || variant.available === false) {
        button.dataset.kessB2bSoldout = "true";
        this.syncQuantity(form?.querySelector(this.selectors.quantity));
        return;
      }

      delete button.dataset.kessB2bSoldout;
      const label = button.querySelector("span");
      const addToCart = button.dataset.kessB2bAtcLabel || this.decode(window.FoxTheme?.variantStrings?.addToCart);
      if (label && addToCart) label.textContent = addToCart;
      this.clearDisabledStyling(button);
      document.querySelectorAll('.sticky-atc-bar [name="add"]').forEach((stickyButton) => {
        this.clearDisabledStyling(stickyButton);
      });

      const quantityInput = form?.querySelector(this.selectors.quantity);
      const fresh = event?.data?.html?.querySelector(`#QuantityForm-${sectionId} .quantity__input`)?.dataset;
      if (fresh) {
        this.applyVariantRule(quantityInput, {
          min: fresh.kessB2bQtyMin,
          increment: fresh.kessB2bQtyIncrement,
          max: fresh.kessB2bQtyMax,
        });
      }

      this.syncButton(button);
      this.syncQuantity(quantityInput);
    },

    /**
     * @param {HTMLElement|null} button - A buy button the product page may have dimmed
     */
    clearDisabledStyling(button) {
      if (!button) return;
      button.style.opacity = "";
      button.style.pointerEvents = "";
    },

    /**
     * @param {string} text - Possibly entity-encoded label
     * @returns {string}
     */
    decode(text) {
      if (!text) return "";
      const holder = document.createElement("div");
      holder.innerHTML = text;
      return holder.textContent || "";
    },
  };

  /**
   * ============================================================================
   * INITIALIZATION
   * ============================================================================
   */

  // Initialize on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      Kess.CartProperties.init();
      Kess.DuoStickConfigurator.init();
      Kess.SetComponents.init();
      Kess.Bundles.init();
      Kess.AnnouncementDiscount.init();
      Kess.ShopCountry.init();
      Kess.GeolocationPopup.init();
      Kess.CartDrawerEnhancements.init();
      Kess.CartUpsellRules.init();
      Kess.B2BBuyControls.init();
    });
  } else {
    Kess.CartProperties.init();
    Kess.DuoStickConfigurator.init();
    Kess.SetComponents.init();
    Kess.Bundles.init();
    Kess.AnnouncementDiscount.init();
    Kess.ShopCountry.init();
    Kess.GeolocationPopup.init();
    Kess.CartDrawerEnhancements.init();
    Kess.CartUpsellRules.init();
    Kess.B2BBuyControls.init();
  }

  document.addEventListener("quick-view:loaded", () => {
    Kess.CartProperties.init();
    Kess.DuoStickConfigurator.init();
    Kess.SetComponents.init();
    Kess.Bundles.init();
  });
})();
